/**
 * Pure equity math functions extracted from UI components for testability.
 * No side effects, no Supabase, no React.
 */

// ─── Anti-Dilution ───────────────────────────────────────────

export interface AntiDilutionInputs {
  originalInvestment: number;
  originalPrice: number;
  originalShares: number;
  newRoundPrice: number;
  newRoundShares: number;
  totalSharesBeforeRound: number;
  /** Percentage of the pre-round capitalisation forming the narrow base. Defaults to 80. */
  narrowBasePct?: number;
}

export interface AntiDilutionResult {
  method: string;
  additionalShares: number;
  newTotalShares: number;
  adjustedPrice: number;
  ownershipPct: number;
}

/**
 * Anti-dilution protection for an existing investor when a new round prices.
 *
 * These are **ratchets down**: they protect against a round priced *below* what
 * the investor paid. On a flat or up round no adjustment happens at all, and the
 * conversion price can never be ratcheted above the original. This previously
 * ran the formulae unconditionally, so an up round returned negative additional
 * shares and an adjusted price higher than the investor originally paid — a
 * $10 round followed by a $20 round reported "-50,000 additional shares".
 *
 * `narrowBasePct` is the share of the pre-round capitalisation counted as the
 * narrow base — narrow-based weighted average excludes options and other
 * unissued securities, and the exact exclusion is a matter of drafting. It
 * defaults to 80%, which is an assumption rather than a derived figure, and any
 * UI presenting the narrow-based result should say so.
 */
export function calcAntiDilution(inputs: AntiDilutionInputs): AntiDilutionResult[] {
  const {
    originalInvestment,
    originalPrice,
    originalShares,
    newRoundPrice,
    newRoundShares,
    totalSharesBeforeRound,
    narrowBasePct = 80,
  } = inputs;
  const totalSharesAfterRound = totalSharesBeforeRound + newRoundShares;

  // Protection only bites when the new round is priced below the original.
  const isDownRound = originalPrice > 0 && newRoundPrice > 0 && newRoundPrice < originalPrice;

  const build = (method: string, ratchetPrice: number): AntiDilutionResult => {
    // Never ratchet the price up, and never take shares away.
    const adjustedPrice =
      isDownRound && ratchetPrice > 0 ? Math.min(ratchetPrice, originalPrice) : originalPrice;
    const entitled = adjustedPrice > 0 ? originalInvestment / adjustedPrice : originalShares;
    const newTotalShares = Math.max(originalShares, entitled);
    const additionalShares = newTotalShares - originalShares;
    const postRoundTotal = totalSharesAfterRound + additionalShares;

    return {
      method,
      additionalShares,
      newTotalShares,
      adjustedPrice,
      ownershipPct: postRoundTotal > 0 ? (newTotalShares / postRoundTotal) * 100 : 0,
    };
  };

  // Shares the new money would have bought at the original price.
  const B = originalPrice > 0 ? (newRoundShares * newRoundPrice) / originalPrice : 0;
  const C = newRoundShares;

  const weightedAverage = (base: number) =>
    base + C > 0 ? originalPrice * ((base + B) / (base + C)) : originalPrice;

  const narrowBase = totalSharesBeforeRound * (Math.min(Math.max(narrowBasePct, 0), 100) / 100);

  return [
    {
      method: 'No Protection',
      additionalShares: 0,
      newTotalShares: originalShares,
      adjustedPrice: originalPrice,
      ownershipPct: totalSharesAfterRound > 0 ? (originalShares / totalSharesAfterRound) * 100 : 0,
    },
    build('Full Ratchet', newRoundPrice),
    build('Broad-Based Weighted Average', weightedAverage(totalSharesBeforeRound)),
    build('Narrow-Based Weighted Average', weightedAverage(narrowBase)),
  ];
}

// ─── SAFE Conversion ─────────────────────────────────────────

export interface SAFEInput {
  investmentAmount: number;
  valuationCap: number | null;
  discountRate: number; // percentage, e.g. 20
  safeType: 'post-money' | 'pre-money';
}

export interface PricedRoundInput {
  preMoney: number;
  newMoney: number;
  pricePerShare: number;
  optionPoolPct: number;
}

export interface SAFEConversionResult {
  conversionPrice: number;
  sharesReceived: number;
  ownershipPct: number;
  effectiveValuation: number;
  discount: number;
}

export interface SAFEConversionOutput {
  results: SAFEConversionResult[];
  founderPct: number;
  newInvestorPct: number;
  optionPoolPct: number;
  totalShares: number;
}

/**
 * Conversion of SAFEs at a priced round.
 *
 * `preRoundFullyDilutedShares` is everything on the register before the round —
 * founders, other existing holders and the existing option pool — excluding both
 * the new money and the SAFEs converting here. This used to be the founders'
 * shares alone, which ignores everyone else on the register, overstates the
 * conversion price and hands the SAFE holder fewer shares than the cap entitles
 * them to.
 *
 * Semantics:
 *  - A **post-money** SAFE's cap is measured against the capitalisation that
 *    includes every converting SAFE and the option pool but excludes the new
 *    money. That is what makes its ownership — investment / cap — a fixed
 *    percentage, which is the whole point of the instrument.
 *  - A **pre-money** SAFE's cap is measured against the capitalisation excluding
 *    the other converting SAFEs, so SAFEs dilute one another.
 *  - Where a SAFE carries both a cap and a discount, the investor takes whichever
 *    yields more shares.
 *  - `optionPoolPct` is the pool as a percentage of the post-round fully diluted
 *    total. Each SAFE is read individually; the previous version applied
 *    `safes[0].safeType` to the whole stack, so one pre-money SAFE at the top of
 *    the list silently changed how every other SAFE converted.
 *
 * Post-money caps make the share counts mutually dependent — each SAFE's shares
 * depend on a total that includes every other SAFE — so the system is solved by
 * iteration rather than in closed form.
 */
export function calcSAFEConversion(
  safes: SAFEInput[],
  pricedRound: PricedRoundInput,
  preRoundFullyDilutedShares: number
): SAFEConversionOutput {
  const existingShares = Math.max(0, preRoundFullyDilutedShares);
  const postMoney = pricedRound.preMoney + pricedRound.newMoney;
  const pricePerShare = pricedRound.pricePerShare;
  const newInvestorShares = pricePerShare > 0 ? pricedRound.newMoney / pricePerShare : 0;
  // A pool at or above 100% of the post-round total has no solution.
  const poolFraction = Math.min(Math.max(pricedRound.optionPoolPct, 0), 99.99) / 100;
  const discountPriceFor = (safe: SAFEInput) =>
    safe.discountRate > 0 && pricePerShare > 0
      ? pricePerShare * (1 - safe.discountRate / 100)
      : null;

  // Shares a SAFE takes, given the capitalisation its cap is measured against.
  const sharesFor = (safe: SAFEInput, postMoneyBasis: number, preMoneyBasis: number) => {
    const basis = safe.safeType === 'post-money' ? postMoneyBasis : preMoneyBasis;
    let shares = 0;
    if (safe.valuationCap && safe.valuationCap > 0 && basis > 0) {
      shares = (safe.investmentAmount * basis) / safe.valuationCap;
    }
    const discountPrice = discountPriceFor(safe);
    if (discountPrice && discountPrice > 0) {
      shares = Math.max(shares, safe.investmentAmount / discountPrice);
    }
    // Neither cap nor discount: converts at the round price like anyone else.
    if (shares === 0 && pricePerShare > 0) shares = safe.investmentAmount / pricePerShare;
    return shares;
  };

  let sharesReceived = safes.map(() => 0);
  let totalShares = (existingShares + newInvestorShares) / (1 - poolFraction);

  for (let iter = 0; iter < 200; iter++) {
    const safeShares = sharesReceived.reduce((sum, s) => sum + s, 0);
    // total = existing + safes + newMoney + pool, so:
    const postMoneyBasis = Math.max(0, totalShares - newInvestorShares); // existing + safes + pool
    const preMoneyBasis = Math.max(0, postMoneyBasis - safeShares); // existing + pool

    const next = safes.map((safe) => sharesFor(safe, postMoneyBasis, preMoneyBasis));
    const nextSafeShares = next.reduce((sum, s) => sum + s, 0);
    const nextTotal = (existingShares + nextSafeShares + newInvestorShares) / (1 - poolFraction);

    const settled = Math.abs(nextTotal - totalShares) <= 1e-9 * Math.max(1, nextTotal);
    sharesReceived = next;
    totalShares = nextTotal;
    if (settled) break;
  }

  const optionPoolShares = poolFraction * totalShares;
  const safeShares = sharesReceived.reduce((sum, s) => sum + s, 0);
  const postMoneyBasis = Math.max(0, totalShares - newInvestorShares);
  const preMoneyBasis = Math.max(0, postMoneyBasis - safeShares);

  const results: SAFEConversionResult[] = safes.map((safe, i) => {
    const shares = sharesReceived[i];
    const conversionPrice = shares > 0 ? safe.investmentAmount / shares : 0;
    const basis = safe.safeType === 'post-money' ? postMoneyBasis : preMoneyBasis;
    const capShares =
      safe.valuationCap && safe.valuationCap > 0 && basis > 0
        ? (safe.investmentAmount * basis) / safe.valuationCap
        : 0;
    const discountPrice = discountPriceFor(safe);
    const discountShares =
      discountPrice && discountPrice > 0 ? safe.investmentAmount / discountPrice : 0;
    const capWon = capShares >= discountShares;

    return {
      conversionPrice,
      sharesReceived: shares,
      ownershipPct: totalShares > 0 ? (shares / totalShares) * 100 : 0,
      effectiveValuation:
        capWon && safe.valuationCap
          ? safe.valuationCap
          : discountPrice
            ? postMoney * (1 - safe.discountRate / 100)
            : postMoney,
      discount: pricePerShare > 0 ? ((pricePerShare - conversionPrice) / pricePerShare) * 100 : 0,
    };
  });

  const pct = (shares: number) => (totalShares > 0 ? (shares / totalShares) * 100 : 0);

  return {
    results,
    founderPct: pct(existingShares),
    newInvestorPct: pct(newInvestorShares),
    optionPoolPct: pct(optionPoolShares),
    totalShares,
  };
}

// ─── Exit Waterfall ──────────────────────────────────────────

// The waterfall lives under supabase/functions/_shared so the sm-exit-waterfall-sim
// edge function runs exactly this code rather than its own copy of it.
export type {
  WaterfallRound,
  WaterfallResult,
} from '../../supabase/functions/_shared/exitWaterfall';
export { calcExitWaterfall } from '../../supabase/functions/_shared/exitWaterfall';
import { calcExitWaterfall } from '../../supabase/functions/_shared/exitWaterfall';

// ─── Exit Waterfall by share class ───────────────────────────

export interface WaterfallShareClass {
  className: string;
  investmentAmount: number;
  shares: number;
  liquidationPreferenceMultiple: number;
  isParticipating: boolean;
  /** Multiple of the investment capping total return. 0 means uncapped. */
  participationCap: number;
  /** Lower number is more senior and is paid first. */
  seniority: number;
}

export interface ClassWaterfallPayout {
  className: string;
  seniority: number;
  liquidationPreference: number;
  preferenceReturn: number;
  participationReturn: number;
  totalReturn: number;
  returnMultiple: number;
}

export interface ClassWaterfallStep {
  step: number;
  className: string;
  stepName: string;
  amountReceived: number;
  cumulativeReceived: number;
  remainingPool: number;
}

/**
 * The same distribution as {@link calcExitWaterfall}, expressed over share classes
 * with an explicit seniority rather than a chronological list of rounds, and with
 * the step-by-step trace a visualisation needs.
 *
 * Classes carrying no preference and no participation right are ordinary common
 * and are pooled together; whatever the residual leaves them is split between
 * them pro-rata by shares.
 */
export function calcClassWaterfall(
  classes: WaterfallShareClass[],
  exitValue: number
): {
  payouts: ClassWaterfallPayout[];
  steps: ClassWaterfallStep[];
  commonSharePrice: number;
} {
  const preferred = classes.filter((c) => c.liquidationPreferenceMultiple > 0 || c.isParticipating);
  const common = classes.filter((c) => c.liquidationPreferenceMultiple <= 0 && !c.isParticipating);
  const commonShares = common.reduce((sum, c) => sum + c.shares, 0);

  // calcExitWaterfall pays the last entry first, so the most senior class (lowest
  // seniority number) has to sit at the end.
  const ordered = [...preferred].sort((a, b) => b.seniority - a.seniority);
  const { results, commonPayout } = calcExitWaterfall(
    ordered.map((c) => ({
      name: c.className,
      investmentAmount: c.investmentAmount,
      liquidationPreference: c.liquidationPreferenceMultiple,
      participating: c.isParticipating,
      participationCap: c.participationCap > 0 ? c.participationCap : null,
      shares: c.shares,
      // Passed through, so two classes given the same seniority are pari passu and
      // share a shortfall pro rata. Without it they were silently stacked in
      // whatever order the sort happened to leave them — a coin toss deciding
      // which investor got paid and which got nothing.
      seniorityRank: c.seniority,
    })),
    commonShares,
    exitValue
  );

  const payouts: ClassWaterfallPayout[] = ordered.map((c, i) => {
    const r = results[i];
    // Residual reaches a round either as participation or, for a non-participating
    // class electing to convert, as common. Both are "beyond the preference".
    const residual = r.participating + r.common;
    const totalReturn = r.liquidationPref + residual;
    return {
      className: c.className,
      seniority: c.seniority,
      liquidationPreference: c.investmentAmount * c.liquidationPreferenceMultiple,
      preferenceReturn: r.liquidationPref,
      participationReturn: residual,
      totalReturn,
      returnMultiple: c.investmentAmount > 0 ? totalReturn / c.investmentAmount : 0,
    };
  });

  common.forEach((c) => {
    const share = commonShares > 0 ? (c.shares / commonShares) * commonPayout : 0;
    payouts.push({
      className: c.className,
      seniority: c.seniority,
      liquidationPreference: 0,
      preferenceReturn: 0,
      participationReturn: share,
      totalReturn: share,
      returnMultiple: c.investmentAmount > 0 ? share / c.investmentAmount : 0,
    });
  });

  payouts.sort((a, b) => a.seniority - b.seniority);

  // Trace: preferences in seniority order, then everything drawn from the residual.
  const steps: ClassWaterfallStep[] = [];
  let pool = Math.max(0, exitValue);
  let step = 1;
  const cumulative = new Map<string, number>();

  const record = (className: string, stepName: string, amount: number) => {
    if (amount <= 0) return;
    pool -= amount;
    const runningTotal = (cumulative.get(className) ?? 0) + amount;
    cumulative.set(className, runningTotal);
    steps.push({
      step: step++,
      className,
      stepName,
      amountReceived: amount,
      cumulativeReceived: runningTotal,
      remainingPool: pool,
    });
  };

  [...payouts]
    .filter((p) => p.preferenceReturn > 0)
    .forEach((p) => {
      const cls = preferred.find((c) => c.className === p.className);
      record(
        p.className,
        `${p.className} Liquidation Preference (${cls?.liquidationPreferenceMultiple ?? 1}x)`,
        p.preferenceReturn
      );
    });

  payouts
    .filter((p) => p.participationReturn > 0)
    .forEach((p) => {
      const cls = preferred.find((c) => c.className === p.className);
      const label = !cls
        ? `${p.className} Pro-Rata Distribution`
        : cls.isParticipating
          ? `${p.className} Participation${cls.participationCap > 0 ? ` (${cls.participationCap}x cap)` : ' (Uncapped)'}`
          : `${p.className} Converted to Common`;
      record(p.className, label, p.participationReturn);
    });

  return {
    payouts,
    steps,
    // What common actually receives per share, not exitValue/totalShares — which
    // ignores the preference stack entirely and overstates it at every exit.
    commonSharePrice: commonShares > 0 ? commonPayout / commonShares : 0,
  };
}

// ─── Convertible Notes ───────────────────────────────────────

export interface ConvertibleNoteInput {
  name: string;
  principalAmount: number;
  /** Annual simple interest, as a percentage. */
  interestRate: number;
  issueDate: Date;
  maturityDate: Date;
  valuationCap: number | null;
  /** Discount to the round price, as a percentage. */
  discountRate: number;
  /**
   * Only `qualified-financing` is modelled. Conversion at maturity and on a change
   * of control turn on terms this calculator has no inputs for — a repayment
   * multiple, whether the note repays rather than converts — so anything else is
   * reported as unmodelled instead of being silently treated as a financing.
   */
  conversionTrigger?: string;
}

export interface NoteRoundInput {
  conversionDate: Date;
  pricePerShare: number;
  preMoney: number;
  newMoney: number;
}

export interface NoteConversionResult {
  noteName: string;
  principalAmount: number;
  accruedInterest: number;
  totalDebt: number;
  conversionPrice: number;
  sharesReceived: number;
  ownershipPct: number;
  effectiveValuation: number;
  daysAccrued: number;
  /** True when the round is dated after the note matured. */
  matured: boolean;
}

export interface NoteConversionOutput {
  results: NoteConversionResult[];
  founderPct: number;
  newInvestorPct: number;
  totalShares: number;
  warnings: string[];
}

const MS_PER_DAY = 86_400_000;

/**
 * Simple interest accrued on a note as at a date.
 *
 * Accrual runs from issue to the **earlier of `asOf` or maturity**, and elapsed
 * days are floored at zero. Exported so the conversion and any interest-over-time
 * chart agree — they previously used separate copies, and both ran past maturity.
 */
export function accruedNoteInterest(
  note: Pick<
    ConvertibleNoteInput,
    'principalAmount' | 'interestRate' | 'issueDate' | 'maturityDate'
  >,
  asOf: Date
): { interest: number; days: number; matured: boolean } {
  const accrualEnd = Math.min(asOf.getTime(), note.maturityDate.getTime());
  const days = Math.max(0, Math.floor((accrualEnd - note.issueDate.getTime()) / MS_PER_DAY));
  return {
    interest: note.principalAmount * (note.interestRate / 100) * (days / 365),
    days,
    matured: asOf.getTime() > note.maturityDate.getTime(),
  };
}

/**
 * Conversion of convertible notes at a priced round.
 *
 * `preRoundFullyDilutedShares` is everything on the register before the round.
 * The cap was previously divided by the founders' shares alone — the same defect
 * as the SAFE path — which overstates the conversion price and short-changes the
 * note holder. Notes are pre-money instruments, so the cap is measured against
 * the pre-round capitalisation and notes dilute one another.
 *
 * Interest is simple, not compounded, and accrues from issue to the **earlier of
 * the conversion date or maturity**. It previously ran to the conversion date
 * unconditionally, so a note ten years past a two-year maturity accrued ten years
 * of interest. A matured note is flagged rather than silently treated as live: at
 * maturity it is repaid, converted or extended, and which of those happened is
 * not something this calculator can know.
 *
 * A conversion dated before issue accrued *negative* interest and reduced the
 * debt. Elapsed days are now floored at zero.
 */
export function calcConvertibleNoteConversion(
  notes: ConvertibleNoteInput[],
  round: NoteRoundInput,
  preRoundFullyDilutedShares: number
): NoteConversionOutput {
  const existingShares = Math.max(0, preRoundFullyDilutedShares);
  const postMoney = round.preMoney + round.newMoney;
  const pricePerShare = round.pricePerShare;
  const newInvestorShares = pricePerShare > 0 ? round.newMoney / pricePerShare : 0;
  const warnings: string[] = [];

  const results: NoteConversionResult[] = notes.map((note) => {
    const {
      interest: accruedInterest,
      days: daysAccrued,
      matured,
    } = accruedNoteInterest(note, round.conversionDate);
    const totalDebt = note.principalAmount + accruedInterest;

    if (matured) {
      warnings.push(
        `${note.name}: matured on ${note.maturityDate.toISOString().slice(0, 10)}, before this round. Interest is accrued to maturity only, and whether it was repaid, converted or extended is not modelled.`
      );
    }

    if (note.conversionTrigger && note.conversionTrigger !== 'qualified-financing') {
      warnings.push(
        `${note.name}: the "${note.conversionTrigger}" trigger is not modelled. The figures below assume conversion at this priced round.`
      );
    }

    const capPrice =
      note.valuationCap && note.valuationCap > 0 && existingShares > 0
        ? note.valuationCap / existingShares
        : null;
    const discountPrice =
      note.discountRate > 0 && pricePerShare > 0
        ? pricePerShare * (1 - note.discountRate / 100)
        : null;

    // The holder takes whichever converts at the lower price.
    const candidates = [capPrice, discountPrice].filter((p): p is number => p !== null && p > 0);
    const conversionPrice = candidates.length > 0 ? Math.min(...candidates) : pricePerShare;

    const sharesReceived = conversionPrice > 0 ? totalDebt / conversionPrice : 0;
    const capWon = capPrice !== null && conversionPrice === capPrice;

    return {
      noteName: note.name,
      principalAmount: note.principalAmount,
      accruedInterest,
      totalDebt,
      conversionPrice,
      sharesReceived,
      ownershipPct: 0,
      effectiveValuation: capWon
        ? (note.valuationCap as number)
        : discountPrice
          ? postMoney * (1 - note.discountRate / 100)
          : postMoney,
      daysAccrued,
      matured,
    };
  });

  const noteShares = results.reduce((sum, r) => sum + r.sharesReceived, 0);
  const totalShares = existingShares + noteShares + newInvestorShares;
  const pct = (shares: number) => (totalShares > 0 ? (shares / totalShares) * 100 : 0);

  results.forEach((r) => {
    r.ownershipPct = pct(r.sharesReceived);
  });

  return {
    results,
    founderPct: pct(existingShares),
    newInvestorPct: pct(newInvestorShares),
    totalShares,
    warnings,
  };
}

// ─── Pay-to-Play ─────────────────────────────────────────────

export interface PayToPlayInputs {
  currentShares: number;
  liquidationPreference: number;
  /** Whether the holder has anti-dilution protection to lose in the first place. */
  hasAntiDilution: boolean;
  /** Whether the holder votes as preferred today. */
  hasPreferredVoting: boolean;
  newRoundSize: number;
  newRoundPrice: number;
  yourProRataAmount: number;
  totalSharesBeforeRound: number;
  participationAmount: number;
  /**
   * Assumed exit price per share. This was hardcoded at $10 regardless of the
   * round price, so pricing a round above $10 silently modelled a *down* exit —
   * at a $50 round it assumed 0.2x — while still reporting a confident figure for
   * what declining to participate would cost.
   */
  exitPricePerShare: number;
  /** Share of pro-rata needed to keep preferred rights. Defaults to 50%. */
  thresholdPct?: number;
}

export interface PayToPlayScenario {
  name: string;
  participates: boolean;
  investedAmount: number;
  sharesAfter: number;
  ownershipAfter: number;
  liquidationPrefAfter: number;
  keepsAntiDilution: boolean;
  keepsPreferredVoting: boolean;
  economicValue: number;
}

export interface PayToPlayOutput {
  scenarios: PayToPlayScenario[];
  /** Derived from the share counts rather than taken as a separate input. */
  currentOwnershipPct: number;
  proRataPct: number;
  minimumThreshold: number;
  dilutionPct: number;
  valueLoss: number;
  newSharesIssued: number;
  exitValuation: number;
}

/**
 * What declining to participate in a round costs a holder under a pay-to-play
 * provision.
 *
 * The holder's preferred value is modelled as **non-participating**: the greater
 * of its liquidation preference or its as-converted share of the exit. The prior
 * version took the preference *and then* a full pro-rata share of the residual,
 * which is participating-preferred behaviour and overstates what most preferred
 * is worth — inflating the penalty for not participating.
 *
 * `hasAntiDilution` and `hasPreferredVoting` describe what the holder has today.
 * They were previously accepted as inputs and never read, so the outcome was
 * identical whether or not the holder had anything to lose.
 */
export function calcPayToPlay(inputs: PayToPlayInputs): PayToPlayOutput {
  const {
    currentShares,
    liquidationPreference,
    hasAntiDilution,
    hasPreferredVoting,
    newRoundSize,
    newRoundPrice,
    yourProRataAmount,
    totalSharesBeforeRound,
    participationAmount,
    exitPricePerShare,
    thresholdPct = 50,
  } = inputs;

  const newSharesIssued = newRoundPrice > 0 ? newRoundSize / newRoundPrice : 0;
  // The round is assumed fully subscribed either way; declining simply means
  // someone else takes the allocation.
  const totalSharesAfter = totalSharesBeforeRound + newSharesIssued;
  const exitValuation = totalSharesAfter * exitPricePerShare;
  const minimumThreshold = yourProRataAmount * (Math.max(0, thresholdPct) / 100);

  const ownership = (shares: number) =>
    totalSharesAfter > 0 ? (shares / totalSharesAfter) * 100 : 0;

  // Non-participating preferred: the greater of the preference or converting.
  const valueOf = (shares: number, preference: number) => {
    const asConverted = exitValuation * (ownership(shares) / 100);
    return preference > 0
      ? Math.max(Math.min(preference, exitValuation), asConverted)
      : asConverted;
  };

  const build = (name: string, invested: number, keepsRights: boolean): PayToPlayScenario => {
    const shares = currentShares + (newRoundPrice > 0 ? invested / newRoundPrice : 0);
    const preference = keepsRights ? liquidationPreference + invested : 0;
    return {
      name,
      participates: keepsRights,
      investedAmount: invested,
      sharesAfter: shares,
      ownershipAfter: ownership(shares),
      liquidationPrefAfter: preference,
      // You cannot keep what you never had.
      keepsAntiDilution: keepsRights && hasAntiDilution,
      keepsPreferredVoting: keepsRights && hasPreferredVoting,
      economicValue: valueOf(shares, preference),
    };
  };

  const full = build('Full Pro-Rata Participation', yourProRataAmount, true);
  const partial = build(
    'Partial Participation',
    participationAmount,
    participationAmount >= minimumThreshold
  );
  const none = build('No Participation', 0, false);

  return {
    scenarios: [full, partial, none],
    currentOwnershipPct:
      totalSharesBeforeRound > 0 ? (currentShares / totalSharesBeforeRound) * 100 : 0,
    proRataPct: newRoundSize > 0 ? (yourProRataAmount / newRoundSize) * 100 : 0,
    minimumThreshold,
    dilutionPct:
      (totalSharesBeforeRound > 0 ? (currentShares / totalSharesBeforeRound) * 100 : 0) -
      none.ownershipAfter,
    valueLoss: full.economicValue - none.economicValue,
    newSharesIssued,
    exitValuation,
  };
}

// ─── ROFR & Co-Sale ──────────────────────────────────────────

export interface ROFRShareholder {
  id: string;
  name: string;
  shares: number;
  hasROFR: boolean;
  /** Lower number is offered the shares first. */
  rofrPriority: number;
  hasCoSaleRight: boolean;
  /**
   * A company ROFR is a right over the *whole* offered block rather than a
   * pro-rata slice of it. Without this the company — which typically holds no
   * shares in its own cap table — has a pro-rata entitlement of zero and its
   * first-priority right can never buy anything.
   */
  isCompany: boolean;
}

export interface ROFRTransferScenario {
  name: string;
  sellerId: string;
  sharesForSale: number;
  pricePerShare: number;
  thirdPartyBuyerName: string;
  /** Exempt transfers (gifts, estate planning) are not offered around first. */
  bypassROFR: boolean;
}

export interface ROFRAllocation {
  shareholderId: string;
  shareholderName: string;
  priority: number;
  proRataEntitlement: number;
  sharesOffered: number;
  sharesAccepted: number;
  amountPaid: number;
  exercisesRight: boolean;
  /** True when the entitlement came from the company's whole-block right. */
  viaCompanyRight: boolean;
}

export interface ROFRCoSaleParticipant {
  shareholderId: string;
  shareholderName: string;
  maxSharesCanSell: number;
  sharesSold: number;
  proceeds: number;
  participates: boolean;
}

/**
 * `completed` — the third-party buyer received the whole block it bid for.
 * `partially-preempted` — ROFR holders took some of it.
 * `fully-preempted` — ROFR holders took all of it and the buyer got nothing.
 */
export type ROFRTransferStatus = 'completed' | 'partially-preempted' | 'fully-preempted';

export type ROFRTimelineStep =
  | { kind: 'offer'; actor: string; shares: number; pricePerShare: number; buyer: string }
  | { kind: 'rofr-triggered' }
  | { kind: 'rofr-bypassed' }
  | {
      kind: 'rofr-exercised';
      actor: string;
      priority: number;
      shares: number;
      amount: number;
      viaCompanyRight: boolean;
    }
  | { kind: 'rofr-declined'; actor: string; priority: number; shares: number }
  | { kind: 'cosale-triggered'; salePercentage: number }
  | { kind: 'cosale-sold'; actor: string; shares: number; amount: number }
  | { kind: 'third-party-purchase'; buyer: string; shares: number; amount: number }
  | { kind: 'outcome'; status: ROFRTransferStatus };

export interface ROFRTransferResult {
  scenarioName: string;
  sellerName: string;
  /** What the seller actually put on the table, after clamping to their holding. */
  sharesForSale: number;
  pricePerShare: number;
  totalValue: number;
  rofrTriggered: boolean;
  rofrAllocations: ROFRAllocation[];
  sharesPreemptedByROFR: number;
  remainingAfterROFR: number;
  coSaleTriggered: boolean;
  coSaleParticipants: ROFRCoSaleParticipant[];
  /** Shares the seller ends up selling to the buyer, after co-sale takes its cut. */
  sellerSharesToThirdParty: number;
  thirdPartyPurchase: number;
  thirdPartyBuyerName: string;
  status: ROFRTransferStatus;
  proceedsDistribution: { recipient: string; shares: number; proceeds: number }[];
  timeline: ROFRTimelineStep[];
  warnings: string[];
}

export interface ROFRTransferOptions {
  /** Share of ROFR holders, highest priority first, who exercise. Clamped to 0–100. */
  rofrExercisePct: number;
  /** Minimum sale, as a % of the seller's own holding, that wakes co-sale rights. */
  coSaleTriggerPct: number;
}

/**
 * Splits `total` whole shares across `weights` in proportion, honouring each
 * party's cap and using largest-remainder rounding so the parts sum to exactly
 * `total`. Whatever a capped party cannot take is redistributed to the parties
 * still under their cap, which is what stops a single participant absorbing a
 * block that several are entitled to share.
 */
function allocateWithCaps(weights: number[], caps: number[], total: number): number[] {
  const out = new Array<number>(weights.length).fill(0);
  let remaining = Math.max(0, Math.floor(total));

  while (remaining > 0) {
    const active = weights.map((_, i) => i).filter((i) => weights[i] > 0 && out[i] < caps[i]);
    if (active.length === 0) break;

    const weightSum = active.reduce((sum, i) => sum + weights[i], 0);
    if (weightSum <= 0) break;

    const exact = active.map((i) => (weights[i] / weightSum) * remaining);
    const grant = exact.map((v) => Math.floor(v));
    let leftover = remaining - grant.reduce((sum, v) => sum + v, 0);
    const byRemainder = exact
      .map((v, k) => ({ k, frac: v - Math.floor(v) }))
      .sort((a, b) => b.frac - a.frac);
    for (let j = 0; j < byRemainder.length && leftover > 0; j++) {
      grant[byRemainder[j].k]++;
      leftover--;
    }

    let progressed = false;
    for (let k = 0; k < active.length; k++) {
      const i = active[k];
      const take = Math.min(grant[k], caps[i] - out[i]);
      if (take > 0) {
        out[i] += take;
        remaining -= take;
        progressed = true;
      }
    }
    if (!progressed) break;
  }

  return out;
}

/**
 * Models a secondary transfer through a right of first refusal and then co-sale
 * (tag-along) rights.
 *
 * Two things worth stating because they drive every number:
 *
 *  - **Who exercises is deterministic.** `rofrExercisePct` is read exactly as its
 *    label claims — the share of ROFR *holders* who exercise — and the holders it
 *    picks are the highest-priority ones. It is not a per-holder coin flip, so the
 *    same inputs always produce the same result and a shown figure can be reproduced.
 *  - **Co-sale takes its shares out of the seller's allocation, pro-rata.** The
 *    buyer wants a fixed block; when tag-along holders join, the block is split
 *    between the seller and those holders in proportion to their holdings. The
 *    seller is always one of the parties in that split, so a tag-along can dilute
 *    the seller's sale but can never displace it entirely.
 *
 * Share counts are allocated as whole shares, so the parts sum exactly and the
 * result never depends on floating-point residue.
 */
export function calcROFRTransfer(
  scenario: ROFRTransferScenario,
  shareholders: ROFRShareholder[],
  options: ROFRTransferOptions
): ROFRTransferResult {
  const warnings: string[] = [];
  const seller = shareholders.find((sh) => sh.id === scenario.sellerId);
  if (!seller) {
    throw new Error(`Seller ${scenario.sellerId} is not in the shareholder list`);
  }

  const price = Math.max(0, scenario.pricePerShare);
  const requested = Math.max(0, Math.floor(scenario.sharesForSale));
  const sellerShares = Math.max(0, Math.floor(seller.shares));

  let offered = requested;
  if (requested > sellerShares) {
    offered = sellerShares;
    warnings.push(
      `${seller.name} holds ${sellerShares.toLocaleString()} shares but the scenario sells ` +
        `${requested.toLocaleString()}. The sale has been capped at the holding.`
    );
  }

  const exercisePct = Math.min(100, Math.max(0, options.rofrExercisePct));
  if (exercisePct !== options.rofrExercisePct) {
    warnings.push('The ROFR exercise rate has been clamped to 0–100%.');
  }

  const timeline: ROFRTimelineStep[] = [
    {
      kind: 'offer',
      actor: seller.name,
      shares: offered,
      pricePerShare: price,
      buyer: scenario.thirdPartyBuyerName,
    },
  ];

  // ── Step 1: ROFR ──
  let remaining = offered;
  const rofrAllocations: ROFRAllocation[] = [];
  const rofrTriggered = !scenario.bypassROFR;

  if (rofrTriggered) {
    timeline.push({ kind: 'rofr-triggered' });

    const rofrHolders = shareholders
      .filter((sh) => sh.hasROFR && sh.id !== scenario.sellerId)
      .filter((sh) => sh.isCompany || sh.shares > 0)
      .sort((a, b) => a.rofrPriority - b.rofrPriority);

    // Deterministic: the top N holders by priority exercise, where N is the
    // requested percentage of the holder count.
    const exercisingCount = Math.round((rofrHolders.length * exercisePct) / 100);
    const exercising = new Set(rofrHolders.slice(0, exercisingCount).map((sh) => sh.id));

    const record = (
      holder: ROFRShareholder,
      entitlement: number,
      viaCompanyRight: boolean
    ): void => {
      const sharesOffered = Math.min(entitlement, remaining);
      const exercisesRight = exercising.has(holder.id);
      const sharesAccepted = exercisesRight ? sharesOffered : 0;
      const amountPaid = sharesAccepted * price;

      rofrAllocations.push({
        shareholderId: holder.id,
        shareholderName: holder.name,
        priority: holder.rofrPriority,
        proRataEntitlement: entitlement,
        sharesOffered,
        sharesAccepted,
        amountPaid,
        exercisesRight,
        viaCompanyRight,
      });

      if (sharesAccepted > 0) {
        remaining -= sharesAccepted;
        timeline.push({
          kind: 'rofr-exercised',
          actor: holder.name,
          priority: holder.rofrPriority,
          shares: sharesAccepted,
          amount: amountPaid,
          viaCompanyRight,
        });
      } else if (sharesOffered > 0) {
        timeline.push({
          kind: 'rofr-declined',
          actor: holder.name,
          priority: holder.rofrPriority,
          shares: sharesOffered,
        });
      }
    };

    const priorities = [...new Set(rofrHolders.map((sh) => sh.rofrPriority))].sort((a, b) => a - b);

    for (const priority of priorities) {
      if (remaining <= 0) break;
      const level = rofrHolders.filter((sh) => sh.rofrPriority === priority);

      // The company's right runs over the whole remaining block.
      for (const holder of level.filter((sh) => sh.isCompany)) {
        if (remaining <= 0) break;
        record(holder, remaining, true);
      }

      const others = level.filter((sh) => !sh.isCompany);
      if (remaining <= 0 || others.length === 0) continue;

      // Everyone at this level is offered a pro-rata slice of what is still
      // available. Slices the decliners leave behind fall through to the next
      // priority, and ultimately to the third-party buyer.
      const block = remaining;
      const weights = others.map((sh) => sh.shares);
      const entitlements = allocateWithCaps(
        weights,
        weights.map(() => block),
        block
      );
      others.forEach((holder, i) => record(holder, entitlements[i], false));
    }
  } else {
    timeline.push({ kind: 'rofr-bypassed' });
  }

  const remainingAfterROFR = remaining;
  const sharesPreemptedByROFR = offered - remainingAfterROFR;

  // ── Step 2: co-sale ──
  const salePercentage = sellerShares > 0 ? (offered / sellerShares) * 100 : 0;
  const coSaleHolders = shareholders.filter(
    (sh) => sh.hasCoSaleRight && sh.id !== scenario.sellerId && sh.shares > 0
  );
  const coSaleTriggered =
    salePercentage >= options.coSaleTriggerPct &&
    remainingAfterROFR > 0 &&
    coSaleHolders.length > 0;
  const coSaleParticipants: ROFRCoSaleParticipant[] = [];
  let sellerSharesToThirdParty = remainingAfterROFR;

  if (coSaleTriggered) {
    timeline.push({ kind: 'cosale-triggered', salePercentage });

    // The seller is a party to the split, not a residual claimant on it.
    const parties = [seller, ...coSaleHolders];
    const weights = parties.map((sh) => Math.max(0, Math.floor(sh.shares)));
    const caps = parties.map((sh, i) =>
      i === 0
        ? remainingAfterROFR
        : Math.min(Math.floor((sh.shares * salePercentage) / 100), Math.floor(sh.shares))
    );

    const allocation = allocateWithCaps(weights, caps, remainingAfterROFR);
    sellerSharesToThirdParty = allocation[0];

    coSaleHolders.forEach((holder, i) => {
      const sharesSold = allocation[i + 1];
      const proceeds = sharesSold * price;
      coSaleParticipants.push({
        shareholderId: holder.id,
        shareholderName: holder.name,
        maxSharesCanSell: caps[i + 1],
        sharesSold,
        proceeds,
        participates: sharesSold > 0,
      });
      if (sharesSold > 0) {
        timeline.push({
          kind: 'cosale-sold',
          actor: holder.name,
          shares: sharesSold,
          amount: proceeds,
        });
      }
    });
  }

  // ── Step 3: the buyer takes whatever survived the offer-around ──
  const thirdPartyPurchase = remainingAfterROFR;
  if (thirdPartyPurchase > 0) {
    timeline.push({
      kind: 'third-party-purchase',
      buyer: scenario.thirdPartyBuyerName,
      shares: thirdPartyPurchase,
      amount: thirdPartyPurchase * price,
    });
  }

  const status: ROFRTransferStatus =
    thirdPartyPurchase >= offered
      ? 'completed'
      : thirdPartyPurchase <= 0
        ? 'fully-preempted'
        : 'partially-preempted';
  timeline.push({ kind: 'outcome', status });

  // Aggregate by recipient so a party that is paid at several steps shows up once.
  const byRecipient = new Map<string, { recipient: string; shares: number; proceeds: number }>();
  const credit = (recipient: string, shares: number, proceeds: number) => {
    if (shares <= 0 && proceeds <= 0) return;
    const row = byRecipient.get(recipient) ?? { recipient, shares: 0, proceeds: 0 };
    row.shares += shares;
    row.proceeds += proceeds;
    byRecipient.set(recipient, row);
  };
  for (const alloc of rofrAllocations) credit(seller.name, alloc.sharesAccepted, alloc.amountPaid);
  credit(seller.name, sellerSharesToThirdParty, sellerSharesToThirdParty * price);
  for (const p of coSaleParticipants) credit(p.shareholderName, p.sharesSold, p.proceeds);

  return {
    scenarioName: scenario.name,
    sellerName: seller.name,
    sharesForSale: offered,
    pricePerShare: price,
    totalValue: offered * price,
    rofrTriggered,
    rofrAllocations,
    sharesPreemptedByROFR,
    remainingAfterROFR,
    coSaleTriggered,
    coSaleParticipants,
    sellerSharesToThirdParty,
    thirdPartyPurchase,
    thirdPartyBuyerName: scenario.thirdPartyBuyerName,
    status,
    proceedsDistribution: [...byRecipient.values()],
    timeline,
    warnings,
  };
}

// ─── Term Sheet Scoring ──────────────────────────────────────

export interface TermSheetTerms {
  preMoney: number;
  investment: number;
  liquidationPrefMultiple: number;
  participating: boolean;
  /** Multiple of investment capping total return. null means uncapped. */
  participationCap: number | null;
  antiDilution: 'none' | 'full-ratchet' | 'broad-based' | 'narrow-based';
  proRataRights: boolean;
  investorBoardSeats: number;
  founderBoardSeats: number;
  independentBoardSeats: number;
  protectiveProvisions: string[];
  dividendPreference: boolean;
  redemptionRights: boolean;
  dragAlongRights: boolean;
  noShopPeriod: number;
  exclusivityDays: number;
  foundersVestingRequired: boolean;
  /** Option pool as a percentage of the post-round fully diluted total. */
  optionPoolSize: number;
  payToPlay: boolean;
  informationRights: boolean;
  registrationRights: 'demand' | 'piggyback' | 'both' | 'none';
}

export interface TermSheetDeduction {
  term: string;
  points: number;
  detail: string;
}

export interface TermSheetScore {
  /** 0–100, where 100 is a term sheet with nothing unfavourable in it. */
  score: number;
  deductions: TermSheetDeduction[];
  /** Points lost before the floor at 0 is applied. */
  rawDeducted: number;
  dilutionPct: number;
  founderBoardControl: boolean;
}

const ANTI_DILUTION_PENALTY: Record<TermSheetTerms['antiDilution'], number> = {
  none: 0,
  'broad-based': 4,
  'narrow-based': 8,
  'full-ratchet': 16,
};

const REGISTRATION_PENALTY: Record<TermSheetTerms['registrationRights'], number> = {
  both: 0,
  demand: 1,
  piggyback: 1,
  none: 3,
};

/**
 * Scores a term sheet from the founder's point of view.
 *
 * **100 means nothing in the sheet is unfavourable**, and every adverse term
 * deducts from there. There are no bonuses, so the scale cannot saturate: the
 * previous version started at 100 and added up to +30 of bonuses before clamping,
 * which put its own shipped default at a raw 106 — dropping anti-dilution
 * altogether took it to 111 and still displayed 100/100, so improvements to a
 * good sheet were invisible.
 *
 * **The score depends only on the sheet itself.** The previous version compared
 * each sheet's pre-money against the average across all sheets, so adding or
 * deleting an unrelated offer silently rescored everything — one offer moved
 * between 37 and 57 on identical terms depending on which rivals sat beside it,
 * while being presented as an absolute "X / 100".
 *
 * **Valuation is deliberately not scored.** Price is not a term, it is the thing
 * the terms are attached to, and it is only meaningful next to the other offers.
 * It belongs in the side-by-side comparison, not in a per-sheet grade. What *is*
 * scored is the dilution the sheet takes, which is a property of the sheet alone.
 *
 * Weights are a judgement, not a derivation. They are expressed as one deduction
 * per term with a reason so the total can be read and argued with rather than
 * taken on faith.
 */
export function scoreTermSheet(sheet: TermSheetTerms): TermSheetScore {
  const deductions: TermSheetDeduction[] = [];
  const deduct = (term: string, points: number, detail: string) => {
    if (points > 0) deductions.push({ term, points, detail });
  };

  // Liquidation preference above a plain 1x.
  const excessPref = Math.max(0, sheet.liquidationPrefMultiple - 1);
  deduct(
    'Liquidation preference',
    excessPref * 12,
    `${sheet.liquidationPrefMultiple}x preference pays the investor before common`
  );

  // Participation, scaled by how tightly it is capped. A 1x cap returns roughly
  // what a non-participating preference would, so it costs almost nothing; an
  // uncapped participation is the full penalty.
  if (sheet.participating) {
    const capped = sheet.participationCap !== null && sheet.participationCap > 0;
    const severity = capped
      ? Math.min(1, Math.max(0, ((sheet.participationCap as number) - 1) / 2))
      : 1;
    deduct(
      'Participating preferred',
      15 * severity,
      capped
        ? `Double dips up to a ${sheet.participationCap}x cap`
        : 'Double dips with no cap: preference first, then shares in the rest'
    );
  }

  deduct(
    'Anti-dilution',
    ANTI_DILUTION_PENALTY[sheet.antiDilution],
    `${sheet.antiDilution} protection repricing on a down round`
  );

  // Board control.
  const totalSeats =
    sheet.investorBoardSeats + sheet.founderBoardSeats + sheet.independentBoardSeats;
  const founderBoardControl = totalSeats > 0 && sheet.founderBoardSeats > totalSeats / 2;
  const investorBoardControl = totalSeats > 0 && sheet.investorBoardSeats > totalSeats / 2;
  if (investorBoardControl) {
    deduct('Board control', 15, 'Investors hold a majority of board seats');
  } else if (totalSeats > 0 && !founderBoardControl) {
    deduct('Board control', 6, 'Neither founders nor investors hold a majority of board seats');
  }

  deduct(
    'Protective provisions',
    Math.min(12, sheet.protectiveProvisions.length * 1.5),
    `${sheet.protectiveProvisions.length} matters subject to an investor veto`
  );

  if (sheet.dividendPreference)
    deduct('Dividend preference', 5, 'Accruing dividends paid ahead of common');
  if (sheet.redemptionRights)
    deduct('Redemption rights', 8, 'Investor can force the company to buy its shares back');
  if (sheet.payToPlay)
    deduct('Pay-to-play', 6, 'Existing holders lose rights if they do not fund the next round');
  if (sheet.foundersVestingRequired)
    deduct('Founder vesting', 4, 'Founders re-vest shares they already hold');
  if (!sheet.proRataRights)
    deduct('No pro-rata rights', 3, 'No right to defend ownership in later rounds');
  if (!sheet.informationRights)
    deduct('No information rights', 2, 'No contractual right to financials or reporting');
  if (sheet.dragAlongRights)
    deduct('Drag-along', 2, 'Market standard, but binds the founder to a sale they did not choose');

  deduct(
    'Registration rights',
    REGISTRATION_PENALTY[sheet.registrationRights],
    `Registration rights: ${sheet.registrationRights}`
  );

  // An option pool above a ~10% norm is dilution taken out of the founders'
  // side of the table before the money arrives.
  deduct(
    'Option pool',
    Math.min(10, Math.max(0, sheet.optionPoolSize - 10) * 0.5),
    `${sheet.optionPoolSize}% pool, above a 10% norm`
  );

  const postMoney = sheet.preMoney + sheet.investment;
  const dilutionPct = postMoney > 0 ? (sheet.investment / postMoney) * 100 : 0;
  deduct(
    'Dilution',
    Math.min(12, Math.max(0, dilutionPct - 20) * 0.4),
    `${dilutionPct.toFixed(1)}% of the company sold in this round`
  );

  if (sheet.noShopPeriod > 45)
    deduct('No-shop period', 3, `${sheet.noShopPeriod} days without talking to anyone else`);
  if (sheet.exclusivityDays > 60)
    deduct('Exclusivity', 3, `${sheet.exclusivityDays} days of exclusivity`);

  const rawDeducted = deductions.reduce((sum, d) => sum + d.points, 0);

  return {
    score: Math.round(Math.max(0, 100 - rawDeducted)),
    deductions: deductions.sort((a, b) => b.points - a.points),
    rawDeducted,
    dilutionPct,
    founderBoardControl,
  };
}

// ─── Vesting ─────────────────────────────────────────────────

export type VestingAccelerationType = 'none' | 'single' | 'double';

export interface VestingGrantTerms {
  totalShares: number;
  startDate: Date;
  cliffMonths: number;
  vestingMonths: number;
  accelerationType: VestingAccelerationType;
  /** Percentage of the *unvested* shares that accelerate. Clamped to 0–100. */
  accelerationPct: number;
  /**
   * The date employment ended, if it has. Vesting stops here: the position is
   * measured as at this date however far forward `asOf` runs.
   *
   * Absent, the schedule runs to completion — which is what this function assumed
   * unconditionally, so a leaver kept accruing shares for ever.
   *
   * Acceleration is a separate question and still applies: a double trigger
   * exists precisely to accelerate *on* an involuntary termination, so the leaver
   * date caps the ordinary accrual and acceleration is then computed against
   * whatever remains unvested at that point.
   */
  terminationDate?: Date;
}

export interface VestingTriggers {
  /** An acquisition or IPO. */
  exitEvent: boolean;
  /** Involuntary termination, the second leg of a double trigger. */
  terminatedWithoutCause: boolean;
}

export interface VestingPosition {
  monthsElapsed: number;
  vestedBeforeAcceleration: number;
  acceleratedShares: number;
  vested: number;
  unvested: number;
  vestedPct: number;
  fullyVestedOn: Date;
  warnings: string[];
}

/**
 * Whole calendar months from `start` to `end`. A month completes once the
 * day-of-month is reached, and the last day of a shorter month completes a month
 * begun on a later day, so 31 Jan → 28 Feb is one month.
 *
 * Verified equal to date-fns `differenceInMonths` across 20,000 random forward
 * date pairs and every month-end edge case. Only meaningful for `end >= start`;
 * callers dealing with earlier dates should handle that case first.
 */
export function wholeMonthsElapsed(start: Date, end: Date): number {
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (months > 0) {
    const lastDayOfEndMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
    const endIsLastDayOfMonth = end.getDate() === lastDayOfEndMonth;
    if (end.getDate() < start.getDate() && !endIsLastDayOfMonth) months -= 1;
  }
  return months;
}

/** Adds whole months to a date, clamping to the end of a shorter month. */
function addWholeMonths(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(date.getDate(), lastDay));
  return target;
}

/**
 * Whether an acceleration clause fires for a given pair of triggers.
 *
 * Single trigger accelerates on the exit alone. Double trigger needs the exit
 * *and* an involuntary termination — a change of control by itself is not the
 * second leg, which is the confusion this naming exists to prevent.
 */
export function vestingAccelerationApplies(
  accelerationType: VestingAccelerationType,
  triggers: VestingTriggers
): boolean {
  if (accelerationType === 'none') return false;
  if (accelerationType === 'single') return triggers.exitEvent;
  return triggers.exitEvent && triggers.terminatedWithoutCause;
}

/**
 * Shares vested under a monthly schedule with a cliff, as at `asOf`.
 *
 * Nothing vests before the cliff; at the cliff the whole elapsed period vests at
 * once, and thereafter it accrues monthly. Acceleration converts a percentage of
 * whatever is still unvested, which is why it can apply before the cliff.
 *
 * The inputs are normalised rather than trusted: share counts and month counts
 * are clamped to non-negative whole numbers, and the acceleration percentage to
 * 0–100. It was previously unclamped and read straight from a `type="number"`
 * field with no bounds, so entering 150% vested 137.5% of a grant and reported a
 * negative unvested balance.
 */
export function calcVestingPosition(
  grant: VestingGrantTerms,
  asOf: Date,
  withAcceleration = false
): VestingPosition {
  const warnings: string[] = [];

  const totalShares = Math.max(0, Math.floor(grant.totalShares));
  const cliffMonths = Math.max(0, Math.floor(grant.cliffMonths));
  const vestingMonths = Math.max(0, Math.floor(grant.vestingMonths));

  let accelerationPct = grant.accelerationPct;
  if (accelerationPct < 0 || accelerationPct > 100) {
    accelerationPct = Math.min(100, Math.max(0, accelerationPct));
    warnings.push(
      `An acceleration of ${grant.accelerationPct}% is not meaningful — a clause can only ` +
        `accelerate shares that are still unvested. Read as ${accelerationPct}%.`
    );
  }
  if (cliffMonths > vestingMonths && vestingMonths > 0) {
    warnings.push(
      `The ${cliffMonths}-month cliff falls after the ${vestingMonths}-month vesting period, ` +
        `so nothing vests until the cliff and everything vests at once when it lands.`
    );
  }

  // Vesting stops at termination. Measuring as at the earlier of `asOf` and the
  // leaving date is the whole of it: everything downstream — the cliff test, the
  // accrual, the unvested balance acceleration is computed against — then falls
  // out correctly. Someone who left before their cliff keeps nothing, which is
  // what a cliff is for.
  const termination = grant.terminationDate;
  const effectiveAsOf = termination && termination.getTime() < asOf.getTime() ? termination : asOf;

  const startedYet = effectiveAsOf.getTime() >= grant.startDate.getTime();
  const monthsElapsed = startedYet ? wholeMonthsElapsed(grant.startDate, effectiveAsOf) : 0;

  let vestedBeforeAcceleration: number;
  if (!startedYet || monthsElapsed < cliffMonths) {
    vestedBeforeAcceleration = 0;
  } else if (monthsElapsed >= vestingMonths) {
    vestedBeforeAcceleration = totalShares;
  } else {
    // Multiply before dividing. `(totalShares / vestingMonths) * monthsElapsed`
    // rounds the monthly rate to a binary float first, and the error survives the
    // multiplication: 2,500,000 over 36 months at month 27 — exactly three
    // quarters — gives 1874999.9999999998, which floors to one share short. The
    // error lands precisely on the round fractions a founder checks. Both
    // operands are integers well inside 2^53, so this form is exact.
    vestedBeforeAcceleration = Math.floor((totalShares * monthsElapsed) / vestingMonths);
  }

  let acceleratedShares = 0;
  if (withAcceleration && grant.accelerationType !== 'none') {
    const stillUnvested = totalShares - vestedBeforeAcceleration;
    acceleratedShares = Math.floor(stillUnvested * (accelerationPct / 100));
  }

  const vested = Math.min(totalShares, vestedBeforeAcceleration + acceleratedShares);

  return {
    // Measured to the same date the accrual was, so a terminated grant reports
    // the months actually served rather than the months since it started.
    monthsElapsed: startedYet ? monthsElapsed : wholeMonthsElapsed(grant.startDate, effectiveAsOf),
    vestedBeforeAcceleration,
    acceleratedShares,
    vested,
    unvested: totalShares - vested,
    vestedPct: totalShares > 0 ? (vested / totalShares) * 100 : 0,
    fullyVestedOn: addWholeMonths(grant.startDate, Math.max(cliffMonths, vestingMonths)),
    warnings,
  };
}

/**
 * Months from `from` until the last grant finishes vesting.
 *
 * The timeline chart used a fixed 60 months from the earliest grant's start,
 * which silently truncated any grant that began more than a year later — and the
 * "add grant" form defaults to today's date, so on the shipped defaults a newly
 * added four-year grant was cut off 31 months early and drawn as though it
 * plateaued at 33%.
 */
export function vestingHorizonMonths(
  grants: Pick<VestingGrantTerms, 'startDate' | 'cliffMonths' | 'vestingMonths'>[],
  from: Date
): number {
  let horizon = 0;
  for (const grant of grants) {
    const months = Math.max(0, Math.floor(grant.cliffMonths), Math.floor(grant.vestingMonths));
    const end = addWholeMonths(grant.startDate, months);
    horizon = Math.max(horizon, wholeMonthsElapsed(from, end));
  }
  // A little headroom so the final flat step is visible rather than clipped.
  return Math.max(12, horizon + 3);
}

// ─── Option Pool Sizing ──────────────────────────────────────

/**
 * Shares to mint so an option pool reaches `target` (a fraction, not a
 * percentage) of the capitalisation *after* the mint.
 *
 * Zero when the pool already sits at or above the target: a refresh tops the
 * pool up, it does not start a fresh one alongside the old one. Getting this
 * wrong is the single most repeated defect in these calculators — three of them
 * minted a whole new tranche each round, so a pool asked to hold 10% drifted
 * past 20% while still being reported as 10%.
 */
export function poolTopUpShares(
  target: number,
  totalShares: number,
  existingPoolShares: number
): number {
  if (target <= 0 || target >= 1) return 0;
  return Math.max(0, (target * totalShares - existingPoolShares) / (1 - target));
}

/**
 * When an option pool is created relative to the round.
 *
 * `pre` carves it out before the share price is struck, so the existing holders
 * carry it and the incoming investor still gets the percentage they paid for.
 * `post` creates it afterwards, diluting the new investor too. `pre` is what an
 * unqualified pool percentage on a term sheet means.
 */
export type PoolTiming = 'pre' | 'post';

export interface PreMoneyPoolRound {
  /** Every share on issue, the existing pool included. */
  totalShares: number;
  /** The part of `totalShares` that is already option pool. */
  poolShares: number;
  preMoney: number;
  newMoney: number;
  /** Target pool as a fraction of the capitalisation *after* the round. */
  target: number;
}

export interface PreMoneyPoolResult {
  poolSharesAdded: number;
  investorShares: number;
  pricePerShare: number;
  totalSharesAfter: number;
  /** Non-empty when the round as specified cannot be priced. */
  warnings: string[];
}

/**
 * The pre-money option pool shuffle, solved rather than approximated.
 *
 * A term sheet's "10% option pool" in a pre-money round means 10% of the
 * capitalisation **after** the round — that is the NVCA convention, and it is
 * precisely what makes the shuffle expensive: the pool is sized against
 * post-money but paid for out of pre-money, by the existing holders alone.
 *
 * `poolTopUpShares(target, totalShares, poolShares)` reaches `target` of the base
 * it is given. Handing it the pre-investment total therefore sizes the pool
 * against the wrong denominator: the investor's shares arrive afterwards and push
 * the pool below the percentage that was asked for. On 8,000,000 founder shares
 * at $8m pre / $2m raise with a "10%" pool, that produced a pool of 8.0%, a price
 * of $0.90 instead of $0.875, and founders shown at 72% when the term sheet
 * leaves them 70% — the error understating dilution, and compounding each round.
 *
 * It also disagreed with `simulate_round`, the engine that actually writes the
 * cap table. Two answers to one question is a defect whichever is preferred.
 *
 * The pool and the price are mutually dependent, so they are solved together:
 * with `s` the non-pool shares, `t` the target and `i` the investor's fraction of
 * post-money, the capitalisation after the round is `s / (1 - t - i)`.
 */
export function solvePreMoneyPoolRound(round: PreMoneyPoolRound): PreMoneyPoolResult {
  const warnings: string[] = [];
  const totalShares = Math.max(0, round.totalShares);
  const poolShares = Math.max(0, Math.min(round.poolShares, totalShares));
  const preMoney = Math.max(0, round.preMoney);
  const newMoney = Math.max(0, round.newMoney);
  const target = round.target > 0 && round.target < 1 ? round.target : 0;

  const postMoney = preMoney + newMoney;
  const investorPct = postMoney > 0 ? newMoney / postMoney : 0;
  const otherShares = totalShares - poolShares;

  if (preMoney <= 0 || totalShares <= 0) {
    if (newMoney > 0) {
      warnings.push(
        'No share price can be struck without a pre-money valuation and shares on issue, so this ' +
          "round's money buys nothing."
      );
    }
    return {
      poolSharesAdded: 0,
      investorShares: 0,
      pricePerShare: 0,
      totalSharesAfter: totalShares,
      warnings,
    };
  }

  const denominator = 1 - target - investorPct;
  if (denominator <= 0) {
    warnings.push(
      `A ${(target * 100).toFixed(0)}% pool alongside an investor taking ` +
        `${(investorPct * 100).toFixed(0)}% leaves nothing for the existing holders, so this round ` +
        'cannot be priced as specified.'
    );
    return {
      poolSharesAdded: 0,
      investorShares: 0,
      pricePerShare: 0,
      totalSharesAfter: totalShares,
      warnings,
    };
  }

  let totalSharesAfter = otherShares / denominator;
  let poolSharesAdded = target * totalSharesAfter - poolShares;

  // A pool already at or above the target needs no top-up, and must not be shrunk
  // to hit it — the shares are granted. Re-solve without the pool term.
  if (poolSharesAdded <= 0) {
    poolSharesAdded = 0;
    totalSharesAfter = investorPct < 1 ? totalShares / (1 - investorPct) : totalShares;
  }

  const investorShares = totalSharesAfter - totalShares - poolSharesAdded;
  const preMoneyShares = totalSharesAfter - investorShares;
  const pricePerShare = preMoneyShares > 0 ? preMoney / preMoneyShares : 0;

  return { poolSharesAdded, investorShares, pricePerShare, totalSharesAfter, warnings };
}

// ─── Option Pool Impact ──────────────────────────────────────

export interface OptionPoolRound {
  roundName: string;
  newMoney: number;
  preMoneyValuation: number;
  /** Target size of the pool, as a percentage of the capitalisation. */
  poolSize: number;
  /**
   * `pre` carves the pool out before the share price is struck, so the existing
   * holders carry it and the incoming investor still gets the percentage they
   * paid for. `post` creates it afterwards, diluting the new investor too.
   */
  poolTiming: PoolTiming;
  /** Whether to top the pool back up to `poolSize` at this round. */
  poolRefresh: boolean;
}

export interface OptionPoolRoundResult {
  roundName: string;
  founderOwnership: number;
  optionPool: number;
  investorOwnership: number;
  pricePerShare: number;
  totalShares: number;
  /** Shares minted into the pool at this round, zero if it was already big enough. */
  poolSharesAdded: number;
  optionPoolShares: number;
  investorShares: number;
  founderShares: number;
}

export interface OptionPoolImpactResult {
  rounds: OptionPoolRoundResult[];
  warnings: string[];
}

/**
 * Founder, pool and investor ownership across a sequence of rounds.
 *
 * Two things this gets right that are easy to get wrong:
 *
 *  - **The pool accumulates.** Shares minted into the pool at an earlier round are
 *    still outstanding at the next one. The previous version overwrote its running
 *    pool total each round while leaving those shares in the share count, so from
 *    the second round on the reported percentages stopped adding up — on the
 *    shipped defaults, 14.16% of the company was unattributed by Series B, and the
 *    pool was reported at 9.00% when it was really 23.16%.
 *  - **A refresh is a top-up, not a fresh pool.** Refreshing to 15% when the pool
 *    already sits at 16% should mint nothing. The previous version minted a whole
 *    new 15% tranche on top of the existing pool regardless, diluting the founders
 *    by ~15% for a top-up that was not needed.
 *
 * Because the pool starts empty, the first round is unaffected by both fixes and
 * its numbers are unchanged.
 *
 * Founder, pool and investor shares are the only classes here, so the three
 * percentages sum to 100 by construction.
 */
export function calcOptionPoolImpact(
  rounds: OptionPoolRound[],
  founderShares: number
): OptionPoolImpactResult {
  const warnings: string[] = [];
  const founderHeld = Math.max(0, founderShares);
  if (founderShares < 0) warnings.push('Founder shares cannot be negative; read as zero.');

  let totalShares = founderHeld;
  let poolShares = 0;
  let investorShares = 0;

  const pctOf = (shares: number) => (totalShares > 0 ? (shares / totalShares) * 100 : 0);

  const results: OptionPoolRoundResult[] = [
    {
      roundName: 'Initial',
      founderOwnership: founderHeld > 0 ? 100 : 0,
      optionPool: 0,
      investorOwnership: 0,
      pricePerShare: 0,
      totalShares,
      poolSharesAdded: 0,
      optionPoolShares: 0,
      investorShares: 0,
      founderShares: founderHeld,
    },
  ];

  const topUpTo = (target: number) => poolTopUpShares(target, totalShares, poolShares);

  for (const round of rounds) {
    let target = round.poolSize / 100;
    if (target < 0 || target >= 1) {
      const clamped = Math.min(0.99, Math.max(0, target));
      warnings.push(
        `${round.roundName}: a pool of ${round.poolSize}% is not achievable — the pool cannot be ` +
          `the whole company. Read as ${(clamped * 100).toFixed(0)}%.`
      );
      target = clamped;
    }

    let poolSharesAdded = 0;
    let pricePerShare: number;
    let newInvestorShares: number;

    if (round.poolRefresh && round.poolTiming === 'pre') {
      // Pool and price are mutually dependent here, so they are solved together
      // rather than sequenced. Topping the pool up against the pre-investment
      // total left it below the percentage asked for and the price above where it
      // should be — see solvePreMoneyPoolRound.
      const solved = solvePreMoneyPoolRound({
        totalShares,
        poolShares,
        preMoney: Math.max(0, round.preMoneyValuation),
        newMoney: Math.max(0, round.newMoney),
        target,
      });
      solved.warnings.forEach((w) => warnings.push(`${round.roundName}: ${w}`));
      poolSharesAdded = solved.poolSharesAdded;
      newInvestorShares = solved.investorShares;
      pricePerShare = solved.pricePerShare;
      poolShares += poolSharesAdded;
      totalShares = solved.totalSharesAfter;
    } else {
      pricePerShare = totalShares > 0 ? Math.max(0, round.preMoneyValuation) / totalShares : 0;
      newInvestorShares = pricePerShare > 0 ? Math.max(0, round.newMoney) / pricePerShare : 0;
      if (pricePerShare <= 0 && round.newMoney > 0) {
        warnings.push(
          `${round.roundName}: no share price can be struck without a pre-money valuation and ` +
            `shares on issue, so this round's money buys nothing.`
        );
      }
      totalShares += newInvestorShares;
    }
    investorShares += newInvestorShares;

    if (round.poolRefresh && round.poolTiming === 'post') {
      const added = topUpTo(target);
      poolSharesAdded += added;
      poolShares += added;
      totalShares += added;
    }

    results.push({
      roundName: round.roundName,
      founderOwnership: pctOf(founderHeld),
      optionPool: pctOf(poolShares),
      investorOwnership: pctOf(investorShares),
      pricePerShare,
      totalShares,
      poolSharesAdded,
      optionPoolShares: poolShares,
      investorShares,
      founderShares: founderHeld,
    });
  }

  return { rounds: results, warnings };
}

// ─── Cap Table Sensitivity ───────────────────────────────────

export interface SensitivityShareholder {
  id: string;
  name: string;
  currentShares: number;
  /** Marks the line the option pool refresh tops up. */
  isOptionPool?: boolean;
}

export interface SensitivityInputs {
  preMoneyValuation: number;
  /** Money raised in the modelled round. */
  newMoney: number;
  /** Pool topped up to this percentage of the post-round capitalisation. */
  poolRefreshPct: number;
  /**
   * When the pool is created. Defaults to `'pre'`, because that is what an
   * unqualified "10% pool" on a term sheet means.
   *
   * This model applied post-money treatment unconditionally and said nothing
   * about it, so it reported founders keeping ~2 points more than a standard
   * term would leave them — the same error, in the same direction, as the one
   * fixed in `calcOptionPoolImpact`. A sensitivity analysis that only shows the
   * founder-friendly variant is not showing the sensitivity.
   */
  poolTiming?: PoolTiming;
}

export interface SensitivityScenarioResult {
  preMoneyValuation: number;
  postMoneyValuation: number;
  newMoney: number;
  /** Derived from the money and the valuation, not supplied alongside them. */
  dilutionPct: number;
  poolRefreshPct: number;
  pricePerShare: number;
  totalShares: number;
  poolSharesAdded: number;
  shareholders: { id: string; name: string; shares: number; ownership: number }[];
  warnings: string[];
}

/**
 * The values a sensitivity variable sweeps.
 *
 * A single step is the minimum, not a division by zero: the previous version
 * computed `(max - min) / (steps - 1)` unguarded, so `steps = 1` produced
 * `Infinity`, and every figure in that scenario came out `NaN`.
 */
export function sensitivityValues(min: number, max: number, steps: number): number[] {
  const count = Math.max(1, Math.floor(steps));
  if (count === 1) return [min];
  const stride = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => min + stride * i);
}

/**
 * One scenario of a cap table sensitivity sweep.
 *
 * **The valuation drives the result.** Previously the scenario was computed from
 * a dilution percentage and a pool percentage alone, and the valuation was
 * assigned, stored on the result and displayed but never used — so the shipped
 * "Pre-Money Valuation" variable swept $8M to $15M across eight steps and
 * produced eight identical cap tables. A sensitivity analyser with no
 * sensitivity on its headline variable.
 *
 * The round is defined the way a round actually is — a pre-money valuation and
 * an amount of money — and the dilution is *derived* from them rather than
 * supplied beside them, so the two can no longer contradict each other.
 *
 * The pool is refreshed to `poolRefreshPct` of the post-round capitalisation,
 * topping up whatever pool already exists rather than minting a fresh one on top
 * of it, and it is identified by an explicit `isOptionPool` flag rather than by
 * matching on a name containing "Option".
 */
export function calcSensitivityScenario(
  shareholders: SensitivityShareholder[],
  inputs: SensitivityInputs
): SensitivityScenarioResult {
  const warnings: string[] = [];
  const preMoney = Math.max(0, inputs.preMoneyValuation);
  const newMoney = Math.max(0, inputs.newMoney);
  const existingShares = shareholders.reduce((sum, sh) => sum + Math.max(0, sh.currentShares), 0);

  let poolPct = inputs.poolRefreshPct / 100;
  if (poolPct < 0 || poolPct >= 1) {
    const clamped = Math.min(0.99, Math.max(0, poolPct));
    warnings.push(
      `A pool of ${inputs.poolRefreshPct}% is not achievable — the pool cannot be the whole ` +
        `company. Read as ${(clamped * 100).toFixed(0)}%.`
    );
    poolPct = clamped;
  }

  const existingPoolShares = shareholders
    .filter((sh) => sh.isOptionPool)
    .reduce((sum, sh) => sum + Math.max(0, sh.currentShares), 0);

  const poolTiming: PoolTiming = inputs.poolTiming ?? 'pre';

  let pricePerShare: number;
  let newMoneyShares: number;
  let poolSharesAdded: number;

  if (poolTiming === 'pre' && poolPct > 0) {
    // Pool and price are mutually dependent — solved together, never sequenced.
    // This model previously applied post-money treatment unconditionally, which
    // reported founders keeping about two points more than a standard term
    // leaves them.
    const solved = solvePreMoneyPoolRound({
      totalShares: existingShares,
      poolShares: existingPoolShares,
      preMoney,
      newMoney,
      target: poolPct,
    });
    solved.warnings.forEach((w) => warnings.push(w));
    pricePerShare = solved.pricePerShare;
    newMoneyShares = solved.investorShares;
    poolSharesAdded = solved.poolSharesAdded;
  } else {
    pricePerShare = existingShares > 0 ? preMoney / existingShares : 0;
    newMoneyShares = pricePerShare > 0 ? newMoney / pricePerShare : 0;
    if (pricePerShare <= 0 && newMoney > 0) {
      warnings.push(
        'No share price can be struck without a pre-money valuation and shares on issue, so this ' +
          'round issues no shares.'
      );
    }
    // Post-money: the pool is created after the round, so the base it is a
    // percentage of already includes the investor's shares.
    poolSharesAdded = poolTopUpShares(poolPct, existingShares + newMoneyShares, existingPoolShares);
  }

  const beforePool = existingShares + newMoneyShares;
  const totalShares = beforePool + poolSharesAdded;

  // The top-up lands on the first pool line only; adding it to each would mint it
  // once per line and break the share count.
  const firstPoolIndex = shareholders.findIndex((sh) => sh.isOptionPool);
  const rows = shareholders.map((sh, i) => {
    const shares = Math.max(0, sh.currentShares) + (i === firstPoolIndex ? poolSharesAdded : 0);
    return {
      id: sh.id,
      name: sh.name,
      shares,
      ownership: totalShares > 0 ? (shares / totalShares) * 100 : 0,
    };
  });

  if (newMoneyShares > 0) {
    rows.push({
      id: '__new_money__',
      name: 'New Investors',
      shares: newMoneyShares,
      ownership: totalShares > 0 ? (newMoneyShares / totalShares) * 100 : 0,
    });
  }

  // A refresh with no pool line to top up creates one.
  if (poolSharesAdded > 0 && firstPoolIndex === -1) {
    rows.push({
      id: '__option_pool__',
      name: 'Option Pool',
      shares: poolSharesAdded,
      ownership: totalShares > 0 ? (poolSharesAdded / totalShares) * 100 : 0,
    });
  }

  const postMoney = preMoney + newMoney;
  return {
    preMoneyValuation: preMoney,
    postMoneyValuation: postMoney,
    newMoney,
    dilutionPct: postMoney > 0 ? (newMoney / postMoney) * 100 : 0,
    poolRefreshPct: poolPct * 100,
    pricePerShare,
    totalShares,
    poolSharesAdded,
    shareholders: rows,
    warnings,
  };
}

// ─── Multi-Round Dilution Forecast ───────────────────────────

export interface DilutionStakeholder {
  id: string;
  name: string;
  type: 'founder' | 'employee' | 'investor';
  initialShares: number;
}

export interface DilutionRound {
  id: string;
  name: string;
  newMoney: number;
  preMoneyValuation: number;
  /** Pool topped up to this percentage. Zero means no refresh at this round. */
  optionPoolRefresh: number;
  poolTiming: PoolTiming;
}

export interface DilutionHolding {
  /** Stable across rounds. Stakeholder id, or `round:<id>` for a round's investors. */
  id: string;
  name: string;
  shares: number;
  ownership: number;
}

export interface DilutionSnapshot {
  roundId: string;
  roundName: string;
  holdings: DilutionHolding[];
  optionPoolShares: number;
  optionPoolPct: number;
  poolSharesAdded: number;
  pricePerShare: number;
  totalShares: number;
}

export interface DilutionForecast {
  snapshots: DilutionSnapshot[];
  warnings: string[];
}

/**
 * Ownership across a sequence of rounds, per stakeholder.
 *
 * **Everyone is tracked by id, never by name.** The previous version used the
 * stakeholder's name as the key of its running share map, so two people called
 * "Founder" collapsed into one entry: a 5,000,000-share holder and a
 * 3,000,000-share holder left 5,000,000 shares simply gone, and the reported
 * ownership summed to 50%. Each round's investors are keyed `round:<id>` for the
 * same reason — two rounds sharing a name used to pool into a single line.
 *
 * **A pool refresh is a top-up**, via {@link poolTopUpShares}. The previous
 * version minted a fresh tranche each round on top of whatever was already
 * there, so a pool asked to hold 10% at every round drifted to 16.21% by the
 * third — and kept minting even when it was already above target.
 *
 * Holdings plus the pool always account for the whole company, so the
 * percentages sum to 100.
 */
export function calcDilutionForecast(
  stakeholders: DilutionStakeholder[],
  rounds: DilutionRound[]
): DilutionForecast {
  const warnings: string[] = [];

  const shares = new Map<string, number>();
  const names = new Map<string, string>();
  for (const holder of stakeholders) {
    shares.set(holder.id, Math.max(0, holder.initialShares));
    names.set(holder.id, holder.name);
  }

  let totalShares = [...shares.values()].reduce((sum, n) => sum + n, 0);
  let poolShares = 0;

  if (totalShares <= 0) {
    warnings.push(
      'No shares are on issue, so there is nothing to dilute and no share price can be struck.'
    );
  }

  const snapshotOf = (
    roundId: string,
    roundName: string,
    pricePerShare: number,
    poolSharesAdded: number
  ): DilutionSnapshot => ({
    roundId,
    roundName,
    holdings: [...shares.entries()].map(([id, held]) => ({
      id,
      name: names.get(id) ?? id,
      shares: held,
      ownership: totalShares > 0 ? (held / totalShares) * 100 : 0,
    })),
    optionPoolShares: poolShares,
    optionPoolPct: totalShares > 0 ? (poolShares / totalShares) * 100 : 0,
    poolSharesAdded,
    pricePerShare,
    totalShares,
  });

  const snapshots: DilutionSnapshot[] = [snapshotOf('initial', 'Initial', 0, 0)];

  for (const round of rounds) {
    let target = round.optionPoolRefresh / 100;
    if (target < 0 || target >= 1) {
      const clamped = Math.min(0.99, Math.max(0, target));
      warnings.push(
        `${round.name}: a pool of ${round.optionPoolRefresh}% is not achievable — the pool cannot ` +
          `be the whole company. Read as ${(clamped * 100).toFixed(0)}%.`
      );
      target = clamped;
    }

    let poolSharesAdded = 0;
    let pricePerShare: number;
    let newInvestorShares: number;

    if (target > 0 && round.poolTiming === 'pre') {
      // Solved together, not sequenced — see solvePreMoneyPoolRound. Sizing the
      // pool against the pre-investment total understated a founder's dilution by
      // about two points a round, compounding.
      const solved = solvePreMoneyPoolRound({
        totalShares,
        poolShares,
        preMoney: Math.max(0, round.preMoneyValuation),
        newMoney: Math.max(0, round.newMoney),
        target,
      });
      solved.warnings.forEach((w) => warnings.push(`${round.name}: ${w}`));
      poolSharesAdded = solved.poolSharesAdded;
      newInvestorShares = solved.investorShares;
      pricePerShare = solved.pricePerShare;
      poolShares += poolSharesAdded;
      totalShares = solved.totalSharesAfter - solved.investorShares;
    } else {
      pricePerShare = totalShares > 0 ? Math.max(0, round.preMoneyValuation) / totalShares : 0;
      newInvestorShares = pricePerShare > 0 ? Math.max(0, round.newMoney) / pricePerShare : 0;
      if (pricePerShare <= 0 && round.newMoney > 0) {
        warnings.push(
          `${round.name}: no share price can be struck without a pre-money valuation and shares on ` +
            `issue, so this round's money buys nothing.`
        );
      }
    }

    if (newInvestorShares > 0) {
      const investorId = `round:${round.id}`;
      shares.set(investorId, (shares.get(investorId) ?? 0) + newInvestorShares);
      names.set(investorId, `${round.name} Investors`);
      totalShares += newInvestorShares;
    }

    if (target > 0 && round.poolTiming === 'post') {
      const added = poolTopUpShares(target, totalShares, poolShares);
      poolSharesAdded += added;
      poolShares += added;
      totalShares += added;
    }

    snapshots.push(snapshotOf(round.id, round.name, pricePerShare, poolSharesAdded));
  }

  return { snapshots, warnings };
}

// ─── Drag-Along & Tag-Along ──────────────────────────────────

export interface DragAlongShareholder {
  id: string;
  name: string;
  shareholderType: 'Founder' | 'Investor' | 'Employee' | 'Other';
  shares: number;
  hasDragAlongRight: boolean;
  hasTagAlongRight: boolean;
  /** Bound to follow a drag-along if one is validly exercised. */
  isDragAlongSubject: boolean;
  /**
   * Money invested, where these shares carry a liquidation preference.
   *
   * Absent, or with no `liquidationPreference`, the holder is treated as ordinary
   * and takes only a residual share — which is what every holder used to get,
   * preference or not.
   */
  investmentAmount?: number;
  /** Multiple of the investment paid ahead of common. Absent or 0 means ordinary. */
  liquidationPreference?: number;
  /** Takes its preference *and* a residual share. */
  participatingPreferred?: boolean;
  /** Multiple of the investment capping total return. Absent means uncapped. */
  participationCap?: number | null;
  /**
   * Lower is more senior; equal ranks are pari passu.
   *
   * Defaults to the same rank for every preferred holder, because this model has
   * no chronology to infer a stack from — its shareholder list is unordered, so
   * inventing a seniority would be arbitrary and would decide, silently, which
   * investor is paid first. Pari passu is the neutral assumption; set the rank
   * explicitly to express a negotiated stack.
   */
  seniorityRank?: number;
}

export interface DragAlongScenario {
  name: string;
  buyerName: string;
  /** What the buyer is paying for the whole company. */
  offerPrice: number;
  /** Shareholder ids proposing the sale. */
  initiatedBy: string[];
  requiresUnanimous: boolean;
}

export interface DragAlongThresholds {
  /** Ownership needed to exercise a drag-along, as a percentage. */
  dragAlongThreshold: number;
  /** Ownership in the initiating group that wakes tag-along rights. */
  tagAlongThreshold: number;
}

export type DragAlongRole = 'Initiator' | 'Dragged Along' | 'Tagged Along' | 'Not Participating';

export interface DragAlongParticipant {
  id: string;
  name: string;
  role: DragAlongRole;
  participates: boolean;
  shares: number;
  ownershipPct: number;
  proceeds: number;
}

export interface DragAlongResult {
  scenarioName: string;
  buyerName: string;
  offerPrice: number;
  /** Derived from the offer and the shares on issue, not entered separately. */
  pricePerShare: number;
  totalShares: number;
  initiatingPct: number;
  dragAlongTriggered: boolean;
  initiatorsHoldDragRight: boolean;
  canForceSale: boolean;
  participants: DragAlongParticipant[];
  totalParticipatingPct: number;
  saleViable: boolean;
  warnings: string[];
}

/**
 * Who is carried into a sale, and what each of them is paid.
 *
 * **Ownership is derived from the share register, never stored beside it.** The
 * previous version kept an `ownershipPct` field alongside `shares` and converted
 * between them against a hardcoded `const totalShares = 10000000`. The shipped
 * cap table happens to total exactly 10,000,000 shares and exactly 100%, so it
 * read correctly until anything changed: on a 40,000,000-share register, setting
 * a holder to 25% wrote them 2,500,000 shares — 6.25% of the company. Editing
 * `shares` never refreshed `ownershipPct` at all, and it is `ownershipPct` that
 * the drag threshold and the viability test are measured against.
 *
 * **The offer drives the price per share.** Both were editable and independent,
 * so they could disagree; proceeds came from the price per share alone, which
 * made the headline offer a display-only field. All three shipped scenarios were
 * internally consistent, so raising the offer from $50M to $500M left every
 * proceeds figure unchanged.
 *
 * Whether a unanimous sale is viable is decided from the participation set
 * rather than by comparing a summed percentage to 100.
 */
export function calcDragAlong(
  shareholders: DragAlongShareholder[],
  scenario: DragAlongScenario,
  thresholds: DragAlongThresholds
): DragAlongResult {
  const warnings: string[] = [];
  const shareOf = (sh: DragAlongShareholder) => Math.max(0, sh.shares);
  const totalShares = shareholders.reduce((sum, sh) => sum + shareOf(sh), 0);

  if (totalShares <= 0) {
    warnings.push(
      'No shares are on issue, so no ownership can be measured and no price per share can be struck.'
    );
  }

  const offerPrice = Math.max(0, scenario.offerPrice);
  const pricePerShare = totalShares > 0 ? offerPrice / totalShares : 0;
  const ownershipOf = (sh: DragAlongShareholder) =>
    totalShares > 0 ? (shareOf(sh) / totalShares) * 100 : 0;

  const initiatorIds = new Set(scenario.initiatedBy);
  const initiatingPct = shareholders
    .filter((sh) => initiatorIds.has(sh.id))
    .reduce((sum, sh) => sum + ownershipOf(sh), 0);

  const dragAlongTriggered = initiatingPct >= thresholds.dragAlongThreshold;
  const initiatorsHoldDragRight = shareholders.some(
    (sh) => initiatorIds.has(sh.id) && sh.hasDragAlongRight
  );
  const canForceSale = dragAlongTriggered && initiatorsHoldDragRight;
  const tagAlongOpen = initiatingPct >= thresholds.tagAlongThreshold;

  const roleOf = (sh: DragAlongShareholder): DragAlongRole => {
    if (initiatorIds.has(sh.id)) return 'Initiator';
    if (canForceSale && sh.isDragAlongSubject) return 'Dragged Along';
    if (tagAlongOpen && sh.hasTagAlongRight) return 'Tagged Along';
    return 'Not Participating';
  };

  /**
   * Split the offer the way a sale actually pays out: through the liquidation
   * waterfall, not flat across share counts.
   *
   * This used to be `shares * (offerPrice / totalShares)` for every participant.
   * That ignores liquidation preferences entirely, and a drag-along is precisely
   * the moment they bite — a holder forced into a sale needs to know what they
   * receive, not what they would receive if no one ranked ahead of them. On a
   * $10M offer with 6,000,000 founder shares and an investor holding 4,000,000
   * shares on $20M at 1x, the flat split reported the founder $6,000,000; the
   * preference takes the whole $10M and the founder receives nothing.
   *
   * Preferred holders with no explicit `seniorityRank` are pari passu — see the
   * note on the field.
   */
  const prefMultipleOf = (sh: DragAlongShareholder) => Math.max(0, sh.liquidationPreference ?? 0);
  const investedOf = (sh: DragAlongShareholder) => Math.max(0, sh.investmentAmount ?? 0);
  const carriesPreference = (sh: DragAlongShareholder) =>
    prefMultipleOf(sh) > 0 && investedOf(sh) > 0;

  const selling = shareholders.filter((sh) => roleOf(sh) !== 'Not Participating');
  const preferredSellers = selling.filter(carriesPreference);
  const ordinarySellers = selling.filter((sh) => !carriesPreference(sh));
  const ordinaryShares = ordinarySellers.reduce((sum, sh) => sum + shareOf(sh), 0);

  const proceedsById = new Map<string, number>();
  if (preferredSellers.length === 0) {
    // Nothing ranks ahead of anything, so the offer is simply split by shares —
    // identical to the previous behaviour, and the common case for a cap table
    // with no preferences recorded.
    const sellingShares = selling.reduce((sum, sh) => sum + shareOf(sh), 0);
    const perShare = sellingShares > 0 ? offerPrice / sellingShares : 0;
    for (const sh of selling) proceedsById.set(sh.id, shareOf(sh) * perShare);
  } else {
    const { results, commonPayout } = calcExitWaterfall(
      preferredSellers.map((sh) => ({
        name: sh.id, // ids, not names, so two holders sharing a name stay distinct
        investmentAmount: investedOf(sh),
        liquidationPreference: prefMultipleOf(sh),
        participating: sh.participatingPreferred === true,
        participationCap:
          sh.participationCap != null && sh.participationCap > 0 ? sh.participationCap : null,
        shares: shareOf(sh),
        seniorityRank: sh.seniorityRank ?? 0,
      })),
      ordinaryShares,
      offerPrice
    );

    for (const r of results) proceedsById.set(r.roundName, r.total);
    const perOrdinaryShare = ordinaryShares > 0 ? commonPayout / ordinaryShares : 0;
    for (const sh of ordinarySellers) proceedsById.set(sh.id, shareOf(sh) * perOrdinaryShare);

    if (ordinaryShares <= 0 && commonPayout > 1e-6) {
      warnings.push(
        'The preferences do not absorb the whole offer, but no ordinary shares are being sold, ' +
          'so the residual has nobody to go to.'
      );
    }
  }

  const participants: DragAlongParticipant[] = shareholders.map((sh) => {
    const role = roleOf(sh);
    const participates = role !== 'Not Participating';
    return {
      id: sh.id,
      name: sh.name,
      role,
      participates,
      shares: shareOf(sh),
      ownershipPct: ownershipOf(sh),
      proceeds: participates ? (proceedsById.get(sh.id) ?? 0) : 0,
    };
  });

  const totalParticipatingPct = participants
    .filter((p) => p.participates)
    .reduce((sum, p) => sum + p.ownershipPct, 0);

  const everyoneIn = participants.length > 0 && participants.every((p) => p.participates);
  const saleViable = scenario.requiresUnanimous ? everyoneIn : everyoneIn || canForceSale;

  return {
    scenarioName: scenario.name,
    buyerName: scenario.buyerName,
    offerPrice,
    pricePerShare,
    totalShares,
    initiatingPct,
    dragAlongTriggered,
    initiatorsHoldDragRight,
    canForceSale,
    participants,
    totalParticipatingPct,
    saleViable,
    warnings,
  };
}

// ─── Option Pool Planning ────────────────────────────────────

export interface PoolAllocationBand {
  role: string;
  level: string;
  /** How many people are planned in this band. */
  count: number;
  sharesPerPerson: number;
}

export interface PoolPlanRound {
  id: string;
  name: string;
  plannedDate: string;
  preMoneyValuation: number;
  newMoneyRaised: number;
  /** Pool topped up to this percentage of the post-round capitalisation. */
  poolRefreshPct: number;
  keyHires: number;
}

export interface PoolPlanInputs {
  /** Fully diluted shares on issue today, the pool included. */
  fullyDilutedShares: number;
  poolAuthorisedShares: number;
  poolGrantedShares: number;
}

export interface PoolProjectionRow {
  round: string;
  date: string;
  poolShares: number;
  grantedShares: number;
  availableShares: number;
  percentAvailable: number;
  /** The pool as a share of the whole company, which is what it dilutes. */
  poolPctOfCompany: number;
  poolSharesAdded: number;
  estimatedGrants: number;
  totalShares: number;
}

export interface PoolPlanResult {
  rows: PoolProjectionRow[];
  /** Shares per hire, weighted by the headcount in each band. */
  averageGrantPerHire: number;
  plannedHeadcount: number;
  plannedShares: number;
  warnings: string[];
}

/**
 * Option pool runway across a sequence of planned rounds.
 *
 * **The share count is an input, not an assumption.** The previous version opened
 * with `let currentTotalShares = 100000000; // Assume base fully diluted shares`
 * and reported every dilution figure against it, with nothing in the UI exposing
 * or contradicting it — a 10,000,000-share pool was reported as 10% dilution of
 * a company nobody had said had 100,000,000 shares. On a register that really
 * holds 10,000,000 shares that pool is half the company.
 *
 * **The average grant is weighted by headcount.** It was the plain mean of each
 * band's `sharesPerPerson`, so the `count` column was displayed and editable but
 * changed nothing. On the shipped plan the two agree to within 2%, which is why
 * it looked right; on a plan of one executive at 500,000 and twenty juniors at
 * 10,000 the unweighted mean is 255,000 against a true 33,333 — 7.6x too high,
 * and every grant-burn projection with it.
 *
 * A refresh tops the pool up via {@link poolTopUpShares} rather than minting a
 * fresh tranche beside it.
 */
export function calcOptionPoolPlan(
  inputs: PoolPlanInputs,
  rounds: PoolPlanRound[],
  allocations: PoolAllocationBand[]
): PoolPlanResult {
  const warnings: string[] = [];

  let totalShares = Math.max(0, inputs.fullyDilutedShares);
  let poolShares = Math.max(0, inputs.poolAuthorisedShares);
  let grantedShares = Math.max(0, inputs.poolGrantedShares);

  if (totalShares <= 0) {
    warnings.push(
      'No fully diluted share count is set, so the pool cannot be expressed as a share of the company.'
    );
  }
  if (grantedShares > poolShares) {
    warnings.push(
      `${Math.round(grantedShares).toLocaleString()} shares are granted from a pool of only ` +
        `${Math.round(poolShares).toLocaleString()}, so the pool is already over-committed.`
    );
  }
  if (totalShares > 0 && poolShares > totalShares) {
    warnings.push(
      `The pool of ${Math.round(poolShares).toLocaleString()} shares is larger than the ` +
        `${Math.round(totalShares).toLocaleString()} shares said to be on issue. The pool is part ` +
        `of the fully diluted count, so one of the two is wrong.`
    );
  }

  const plannedHeadcount = allocations.reduce((sum, a) => sum + Math.max(0, a.count), 0);
  const plannedShares = allocations.reduce(
    (sum, a) => sum + Math.max(0, a.count) * Math.max(0, a.sharesPerPerson),
    0
  );
  const averageGrantPerHire = plannedHeadcount > 0 ? plannedShares / plannedHeadcount : 0;
  if (allocations.length > 0 && plannedHeadcount <= 0) {
    warnings.push(
      'The allocation plan has no headcount, so no average grant can be drawn from it and hiring ' +
        'is projected to consume nothing.'
    );
  }

  const rowFor = (
    round: string,
    date: string,
    poolSharesAdded: number,
    estimatedGrants: number
  ): PoolProjectionRow => {
    const availableShares = poolShares - grantedShares;
    return {
      round,
      date,
      poolShares,
      grantedShares,
      availableShares,
      percentAvailable: poolShares > 0 ? (availableShares / poolShares) * 100 : 0,
      poolPctOfCompany: totalShares > 0 ? (poolShares / totalShares) * 100 : 0,
      poolSharesAdded,
      estimatedGrants,
      totalShares,
    };
  };

  const rows: PoolProjectionRow[] = [rowFor('Current', '', 0, 0)];

  for (const round of rounds) {
    const preMoney = Math.max(0, round.preMoneyValuation);
    const newMoney = Math.max(0, round.newMoneyRaised);
    const newInvestorShares = preMoney > 0 ? (totalShares * newMoney) / preMoney : 0;
    if (preMoney <= 0 && newMoney > 0) {
      warnings.push(
        `${round.name}: without a pre-money valuation there is no price, so this round issues no shares.`
      );
    }

    let target = round.poolRefreshPct / 100;
    if (target < 0 || target >= 1) {
      const clamped = Math.min(0.99, Math.max(0, target));
      warnings.push(
        `${round.name}: a pool of ${round.poolRefreshPct}% is not achievable — the pool cannot be ` +
          `the whole company. Read as ${(clamped * 100).toFixed(0)}%.`
      );
      target = clamped;
    }

    const postMoneyFD = totalShares + newInvestorShares;
    const poolSharesAdded = poolTopUpShares(target, postMoneyFD, poolShares);
    poolShares += poolSharesAdded;
    totalShares = postMoneyFD + poolSharesAdded;

    const estimatedGrants = Math.max(0, round.keyHires) * averageGrantPerHire;
    grantedShares += estimatedGrants;

    rows.push(rowFor(round.name, round.plannedDate, poolSharesAdded, estimatedGrants));
  }

  return { rows, averageGrantPerHire, plannedHeadcount, plannedShares, warnings };
}

// ─── Ownership Projection ────────────────────────────────────

export interface ProjectionRound {
  closedAt: string | null;
  totalAmount: number | null;
  preMoneyValuation: number | null;
}

export interface ProjectionFinancials {
  monthlyBurn: number | null;
  cashOnHand: number | null;
}

/** Something the projection had to invent because the data was not there. */
export interface ProjectionAssumption {
  field: 'roundInterval' | 'dilution' | 'roundSize' | 'runway';
  value: number;
  reason: string;
}

/** How much of the projection rests on observation rather than assumption. */
export type ProjectionEvidence = 'none' | 'weak' | 'moderate' | 'strong';

export interface ProjectionAnalysis {
  totalRounds: number;
  observedIntervals: number;
  observedDilutions: number;
  observedRoundSizes: number;
  avgMonthsBetweenRounds: number;
  avgDilution: number;
  avgRoundSize: number;
  runwayMonths: number | null;
  monthsToNextRound: number;
  totalRaised: number;
  lastRoundDate: string | null;
  assumptions: ProjectionAssumption[];
  evidence: ProjectionEvidence;
}

export interface ProjectionScenarioResult {
  name: 'Conservative' | 'Moderate' | 'Aggressive';
  description: string;
  nextRoundMonths: number;
  roundSize: number;
  dilution: number;
  projectedOwnership: number;
  confidence: 'high' | 'medium' | 'low';
}

const DEFAULT_MONTHS_BETWEEN_ROUNDS = 18;
const DEFAULT_DILUTION_PCT = 20;
const DEFAULT_ROUND_SIZE = 1_000_000;

const parseDate = (value: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * What a company's funding history says about how it raises.
 *
 * **Every invented number is reported.** The previous version fell back silently
 * three times — 18 months between rounds, 20% dilution, a $1,000,000 round — so a
 * company with a single round on file produced a full projection in which nothing
 * had been measured, and the middle scenario was labelled "high confidence"
 * because that string was hardcoded. Confidence is now derived from how many
 * observations the analysis actually rests on.
 *
 * **A round's size is not a valuation.** Dilution used
 * `prev.pre_money_valuation || prev.total_amount || 5000000`, so a missing
 * pre-money silently became the amount raised — a $2M round read as a $2M
 * pre-money, turning a realistic 33.3% average into 71.4%. A pair with no
 * recorded pre-money now contributes no dilution observation at all, which is
 * the honest answer, and says so.
 *
 * Rounds with an unparseable or missing close date are skipped rather than
 * treated as 1 January 1970, which previously produced a 648-month interval.
 */
export function analyseFundingHistory(
  rounds: ProjectionRound[],
  financials: ProjectionFinancials | null
): ProjectionAnalysis {
  const assumptions: ProjectionAssumption[] = [];
  const intervals: number[] = [];
  const dilutions: number[] = [];
  const sizes: number[] = [];
  let missingPreMoney = 0;
  let missingDates = 0;

  // Gaps are a property of adjacent rounds.
  for (let i = 1; i < rounds.length; i++) {
    const prevDate = parseDate(rounds[i - 1].closedAt);
    const currDate = parseDate(rounds[i].closedAt);
    if (prevDate && currDate) {
      const months = wholeMonthsElapsed(prevDate, currDate);
      if (months > 0) intervals.push(months);
    } else {
      missingDates++;
    }
  }

  // Dilution and size are properties of a single round. The previous version
  // paired each round's amount with the *preceding* round's pre-money, which is
  // not that round's dilution and roughly doubled it on a normal history: a $2M
  // raise on its own $10M pre-money is 16.7%, not the 33.3% you get by dividing
  // it into the round before.
  for (const r of rounds) {
    const raised = r.totalAmount ?? 0;
    if (raised <= 0) continue;
    sizes.push(raised);
    const preMoney = r.preMoneyValuation;
    if (preMoney && preMoney > 0) {
      dilutions.push((raised / (preMoney + raised)) * 100);
    } else {
      missingPreMoney++;
    }
  }

  const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;

  const avgMonthsBetweenRounds =
    intervals.length > 0 ? mean(intervals) : DEFAULT_MONTHS_BETWEEN_ROUNDS;
  if (intervals.length === 0) {
    assumptions.push({
      field: 'roundInterval',
      value: DEFAULT_MONTHS_BETWEEN_ROUNDS,
      reason:
        rounds.length < 2
          ? 'Only one round is on file, so there is no gap between rounds to measure.'
          : 'No two rounds have usable close dates, so the gap between rounds could not be measured.',
    });
  }

  const avgDilution = dilutions.length > 0 ? mean(dilutions) : DEFAULT_DILUTION_PCT;
  if (dilutions.length === 0) {
    assumptions.push({
      field: 'dilution',
      value: DEFAULT_DILUTION_PCT,
      reason:
        missingPreMoney > 0
          ? 'No round has a recorded pre-money valuation, so past dilution could not be measured.'
          : 'There is no prior round to measure dilution against.',
    });
  } else if (missingPreMoney > 0) {
    assumptions.push({
      field: 'dilution',
      value: avgDilution,
      reason: `${missingPreMoney} round${missingPreMoney === 1 ? '' : 's'} had no pre-money valuation and were left out of the dilution average.`,
    });
  }

  const avgRoundSize = sizes.length > 0 ? mean(sizes) : DEFAULT_ROUND_SIZE;
  if (sizes.length === 0) {
    assumptions.push({
      field: 'roundSize',
      value: DEFAULT_ROUND_SIZE,
      reason: 'No round has a recorded amount, so a typical round size could not be measured.',
    });
  }

  if (missingDates > 0) {
    assumptions.push({
      field: 'roundInterval',
      value: avgMonthsBetweenRounds,
      reason: `${missingDates} round${missingDates === 1 ? '' : 's'} had no usable close date and were left out of the interval average.`,
    });
  }

  const burn = financials?.monthlyBurn ?? 0;
  const cash = financials?.cashOnHand ?? 0;
  const runwayMonths = burn > 0 && cash > 0 ? Math.floor(cash / burn) : null;

  let monthsToNextRound: number;
  if (runwayMonths !== null) {
    // Raise about three months before the money runs out.
    monthsToNextRound = Math.max(3, runwayMonths - 3);
  } else {
    monthsToNextRound = avgMonthsBetweenRounds;
    assumptions.push({
      field: 'runway',
      value: avgMonthsBetweenRounds,
      reason:
        'No burn rate and cash balance are recorded, so the next raise is timed from the historical cadence rather than from runway.',
    });
  }

  // How much of this rests on measurement rather than assumption.
  const observations = intervals.length + dilutions.length + sizes.length;
  const evidence: ProjectionEvidence =
    observations === 0
      ? 'none'
      : observations <= 2
        ? 'weak'
        : observations <= 5
          ? 'moderate'
          : 'strong';

  const lastRound = [...rounds].reverse().find((r) => parseDate(r.closedAt) !== null);

  return {
    totalRounds: rounds.length,
    observedIntervals: intervals.length,
    observedDilutions: dilutions.length,
    observedRoundSizes: sizes.length,
    avgMonthsBetweenRounds,
    avgDilution,
    avgRoundSize,
    runwayMonths,
    monthsToNextRound,
    totalRaised: rounds.reduce((sum, r) => sum + (r.totalAmount ?? 0), 0),
    lastRoundDate: lastRound?.closedAt ?? null,
    assumptions,
    evidence,
  };
}

/**
 * Three projections of where ownership lands at the next round.
 *
 * The conservative and aggressive cases are the historical average scaled by
 * 0.7 and 1.3 — a spread chosen to bracket the central case, not derived from
 * anything, which is why each scenario carries its description.
 *
 * Confidence follows the evidence behind the analysis rather than being fixed
 * per scenario, and dilution is capped at 100% so a heavily diluted history
 * cannot project a negative shareholding.
 */
export function projectOwnership(
  analysis: ProjectionAnalysis,
  currentOwnership: number
): ProjectionScenarioResult[] {
  const held = Math.max(0, currentOwnership);
  const rank: ProjectionEvidence[] = ['none', 'weak', 'moderate', 'strong'];
  const evidenceIndex = rank.indexOf(analysis.evidence);
  const centralConfidence: ProjectionScenarioResult['confidence'] =
    analysis.evidence === 'strong' ? 'high' : analysis.evidence === 'moderate' ? 'medium' : 'low';
  // The bracketing cases are a step less certain than the central one.
  const outerConfidence: ProjectionScenarioResult['confidence'] =
    evidenceIndex >= 3 ? 'medium' : 'low';

  const build = (
    name: ProjectionScenarioResult['name'],
    description: string,
    dilutionMultiplier: number,
    monthsMultiplier: number,
    sizeMultiplier: number,
    confidence: ProjectionScenarioResult['confidence']
  ): ProjectionScenarioResult => {
    const dilution = Math.min(100, Math.max(0, analysis.avgDilution * dilutionMultiplier));
    return {
      name,
      description,
      nextRoundMonths: Math.max(1, Math.ceil(analysis.monthsToNextRound * monthsMultiplier)),
      roundSize: Math.max(0, analysis.avgRoundSize * sizeMultiplier),
      dilution,
      projectedOwnership: held * (1 - dilution / 100),
      confidence,
    };
  };

  return [
    build(
      'Conservative',
      'Lower dilution scenario (70% of the historical average)',
      0.7,
      1.2,
      0.8,
      outerConfidence
    ),
    build('Moderate', 'Based on historical average patterns', 1, 1, 1, centralConfidence),
    build(
      'Aggressive',
      'Higher dilution scenario (130% of the historical average)',
      1.3,
      0.8,
      1.5,
      outerConfidence
    ),
  ];
}

// ─── Dilution Calculator ─────────────────────────────────────

export function calcDilution(
  existingShares: number,
  newShares: number
): { preOwnership: number; postOwnership: number; dilutionPct: number } {
  const totalAfter = existingShares + newShares;
  const preOwnership = 100;
  const postOwnership = (existingShares / totalAfter) * 100;
  const dilutionPct = preOwnership - postOwnership;
  return { preOwnership, postOwnership, dilutionPct };
}
